import React, { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from '@wry/equality';
import * as zod from 'zod';
import { isMasterScenario, MASTER_SCENARIO_CLIENTID } from './Scenario';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import {
  Badge,
  Box,
  Button,
  DeleteButton,
  DropdownButton,
  FormField,
  FormRow,
  Input,
  Menu,
  Modal,
  ModalBody,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
  Tooltip,
} from './ui';
import type { ModalProps } from './ui/Modal/Modal';
import type {
  AllVesselAttributesQuery,
  Currency,
  FreightCostsQuery,
  FreightCostsQueryVariables,
  ScenarioTimeline_OrderFragment,
  ScenarioTimeline_OrganizationFragment,
} from '../graphql';
import { FreightCostsDocument, useAllVesselAttributesQuery } from '../graphql';
import { getVesselCapacities } from './core/Vessel';
import type { CalculationConfig } from './core/ScheduleDataContext';
import type { Vessel } from './core/Vessel';
import type { CoreEventType } from './core/TimeData';
import { PortCallEventType } from './core/TimeData';
import { isLoadEvent } from './core/TimeData';
import type { ScheduleDataContext } from './core/ScheduleDataContext';
import { isTimeData, isLeg } from './core/TimeData';
import type { CargoEvent } from './core/TimeData';
import type { PortCallEvent, Events } from './core/TimeData';
import {
  isCargoEventTimeData,
  isPortCallEventTimeData,
  isLoadEventTimeData,
} from './core/TimeData';
import type { TimeData } from './core/TimeData';
import { msToD } from '../lib/time';
import { getTDIncomeAndMassDistance } from './shipment/income/income';
import {
  isDefined,
  isInRange,
  setDifference,
  setIntersection,
} from '../lib/util';
import {
  formatCurrency,
  formatNumber,
  parseCurrencyString,
} from '../lib/format';
import type { EventTimeline } from './core/Timelines';
import { getSubTimeline } from './core/Timelines';
import type {
  GetScenarioData,
  MasterScenarioData,
  CommittedPlanningData,
  UserScenarioData,
} from '../lib/planningData';
import { type ScenarioOrder } from '../lib/planningData';
import { usePortCallCosts } from './shipment/costs/costs';
import moment from 'moment';
import type { ClientId } from './core/ClientId';
import { useLocalSetting } from './hooks/useLocalConfig';
import DateRangePicker from './ui/DateRangePicker';
import { useDistances } from './distances/hooks';
import { incomeForLoadedAmountGetter } from './shipment/income/income';
import { calculateTce } from './shipment/TCE';
import { useApolloClient } from '@apollo/client';
import useEvent from './hooks/useEvent';
import { CompareScenarioTimelines } from './timeline/ScenarioMergeTimeline';
import { MenuSeparator, MenuToggle } from './ui/Menu/Menu';
import { useBunkerPrices } from './hooks/useBunkerPrices';
import LoadingSpinner from './LoadingSpinner';
import type { VoyageRange } from './core/core.compare';
import {
  fitRangeOutsideOfVoyages,
  getEventsDifferenceTimerange,
  getBallastToBallastRanges,
  squashVoyageGroup,
} from './core/core.compare';
import {
  hasFeature,
  useCurrentUser,
  useDefaultCurrency,
} from '../state/zustand';
import type { PnlData } from './hooks/usePnlData';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { persistedAtom } from '../state/utils/persistedAtom';

import { useOnce } from './hooks/misc';
import type { ResetAssignmentsToMasterOpts } from '../pages/planning';
import { getAssignedOrderIds } from '@seaber/common/core/scenario';
import { DefaultMap } from '@seaber/common/lib/collections';
import { sumBy } from 'lodash';
import { getExtraCostsForLoadedAmount } from './shipment/costs/extraCost';
import { getFreightsByOrderId } from './shipment/income/orderFreight';
import {
  calculateFreightCosts,
  type FreightCosts,
} from './shipment/costs/freightCost';
import { useCalculateTimeDataPnl } from './pnl/ui/context';
import type { CalculatePnlResult } from './pnl/pnl';

export interface ScenarioComparisonProps extends ModalProps {
  scenarios: readonly ScenarioSchedule[];
  orderById: Map<number, ScenarioTimeline_OrderFragment>;
  vessels: ReadonlyArray<Vessel>;
  calculationConfig: CalculationConfig;
  currentUserOrgId: number;
  organizationsById: Map<number, ScenarioTimeline_OrganizationFragment>;
  pnlData: PnlData;
  selectedScenarioId: ClientId;

  masterData: MasterScenarioData;
  selectedScenarioData: UserScenarioData;
  committedData: CommittedPlanningData;
  getScenarioData: GetScenarioData;

  onResetAssignmentsToMaster: (items: ResetAssignmentsToMasterOpts) => void;
  onConfirmEventOverwrite: (events: TimeData[]) => void;
  sharedOrderIds: Set<number>;
  scheduleContext: ScheduleDataContext;
}

enum ScenarioOrderChangeType {
  'ADDED',
  'UPDATED',
  'REMOVED',
  'COMMON_ADDED',
}

/** Name clash. Does not relate to the ScenarioOrder type. */
interface ScenarioOrderChange {
  order: ScenarioTimeline_OrderFragment;
  changeType: ScenarioOrderChangeType;
}

const ScenarioOrder = ({
  order,
  changeType,
}: ScenarioOrderChange): JSX.Element => {
  const background = {
    [ScenarioOrderChangeType.ADDED]: '#bed8ef',
    [ScenarioOrderChangeType.UPDATED]: '#bed8ef',
    [ScenarioOrderChangeType.REMOVED]: '#fdd8c7',
    [ScenarioOrderChangeType.COMMON_ADDED]: '#0000',
  }[changeType];

  return (
    <tr style={{ background }}>
      <td>
        <Box py={2} pl={3}>
          <Text mr={2}>{order.id}</Text>
        </Box>
      </td>
      <td>
        <Text mr={2}>
          {order.locationByLoadPortId?.name} -{' '}
          {order.locationByDischargePortId?.name}
        </Text>
      </td>
      <td>
        <Text mr={2}>{order.assortmentByAssortmentId?.name}</Text>
      </td>
      <td>
        <Box>
          <Text>{order.cargoAmount}</Text>
        </Box>
      </td>
    </tr>
  );
};

const ScenarioOrderTable = ({
  orderChanges,
}: {
  orderChanges: ScenarioOrderChange[];
}): JSX.Element => {
  return (
    <table style={{ width: '100%', borderSpacing: '0' }}>
      <tbody>
        {orderChanges.map((orderChange) => (
          <ScenarioOrder key={orderChange.order.id} {...orderChange} />
        ))}
      </tbody>
    </table>
  );
};

const GoodValueDot = () => (
  <Box
    width="0.8em"
    height="0.8em"
    display="inline-block"
    backgroundColor="green"
    borderRadius="1em"
    marginRight="0.5em"
  />
);

interface ComparisonData {
  arrivalHitRate: number;
  averageVesselFillRateMass: number;
  averageVesselFillRateVolume: number;
  ballastPercentage: number;
  totalBallastDistance: number;
  totalBunkerCost: number;
  totalCargoMass: number;
  totalCargoVolume: number;
  totalDuration: number;
  totalLadenDistance: number;
  totalPortDuration: number;
  totalSeaDuration: number;
  totalTcCost: number;
  totalTcCostPerTonMile: number;
  totalTonMiles: number;
  effectiveTonMiles: number;
  totalChannelCost: number;
  comparisonDataByTimeline?: Map<EventTimeline, TimelineComparisonData>;
}

const isPortCallEventOfTypeTimeData = <T extends PortCallEventType>(
  td: TimeData<Events>,
  type: T,
): td is TimeData<PortCallEvent> & {
  event: TimeData<PortCallEvent>['event'] & {
    event: T;
  };
} => {
  return isPortCallEventTimeData(td) && td.event.event === type;
};

const isArrivalInTime = (td: TimeData<CargoEvent>) => {
  const { cancelling, layday } = td.event.order;
  if (cancelling == null && layday == null) return true;
  const start = layday ? new Date(layday).getTime() : -Infinity;
  const end = cancelling ? new Date(cancelling).getTime() : Infinity;
  return isInRange(td.arriveInPort, { start, end });
};

type TimelineComparisonData = {
  totalDuration: number;
  totalArrivalsInTime: number;
  totalSeaDuration: number;
  massCapacityUsageByLadenLeg: number[];
  totalLoadedMass: number;
  totalBallastDistance: number;
  totalBunkerCost: number;
  totalLadenDistance: number;
  totalPortDuration: number;
  volumeCapacityUsageByLadenLeg: number[];
  tcCost: number;
  totalLoadedOrders: number;
  totalDistance: number;
  totalLoadedVolume: number;
  totalTonMiles: number;
  totalChannelCost: number;
};

const getTimelineComparisonData = (
  timeline: EventTimeline,
): TimelineComparisonData => {
  const { vessel, voyageGroups } = timeline;

  const { massCapacity, volumeCapacity } = getVesselCapacities(vessel);

  const allEventsAndLegs = voyageGroups.flatMap((vg) => vg.eventsAndLegs);
  const firstStartTime = allEventsAndLegs[0]?.start ?? 0;
  const lastEndTime = allEventsAndLegs[allEventsAndLegs.length - 1]?.end ?? 0;
  const totalDuration = lastEndTime - firstStartTime;

  let totalLoadedOrders = 0;
  let totalDistance = 0;
  let totalLadenDistance = 0;
  let totalLoadedMass = 0;
  let totalLoadedVolume = 0;
  let totalSeaDuration = 0;
  let totalArrivalsInTime = 0;
  let totalTonMiles = 0;
  let totalChannelCost = 0;
  let totalBunkerCost = 0;
  let prevDeparture: TimeData<PortCallEvent> | null = null;
  const massCapacityUsageByLadenLeg: number[] = [];
  const volumeCapacityUsageByLadenLeg: number[] = [];

  voyageGroups.forEach((vg) => {
    vg.eventsAndLegs.forEach((td, i) => {
      if (isTimeData(td)) {
        if (isPortCallEventOfTypeTimeData(td, PortCallEventType.Departure)) {
          prevDeparture = td;
        }
        totalChannelCost += td.costs.channelCost;
        totalBunkerCost += td.costs.bunkerGrade.total;

        if (isCargoEventTimeData(td)) {
          if (isLoadEvent(td.event)) {
            totalLoadedOrders += 1;
            totalLoadedMass += td.mass;
            totalLoadedVolume += td.volume;

            if (isArrivalInTime(td)) totalArrivalsInTime += 1;
          }
        }
      }

      // If leg at index 0 is laden ignore it as it means the vessel has arrived at the port during
      // previous shipment that is not in the current comparison scope
      if (isLeg(td) && !(i === 0 && td.laden)) {
        totalDistance += td.distance;
        totalSeaDuration += td.end - td.start;

        if (td.laden) {
          totalLadenDistance += td.distance;

          if (!prevDeparture) {
            console.error(
              'Unable to get laden leg ton miles and mass capacity usage, previous departure event not available.',
              td,
            );
          } else {
            const { usedMassCapacity, usedVolumeCapacity } = prevDeparture;

            totalTonMiles += usedMassCapacity * td.distance;

            const usedMassCapacityPercentage =
              (usedMassCapacity / massCapacity) * 100;
            const usedVolumeCapacityPercentage =
              (usedVolumeCapacity / volumeCapacity) * 100;

            massCapacityUsageByLadenLeg.push(usedMassCapacityPercentage);
            volumeCapacityUsageByLadenLeg.push(usedVolumeCapacityPercentage);
          }
        }
      }
    });

    // With TC all events *should* be in the same voyageGroup so this is not inherently necessary
    prevDeparture = null;
  });

  const totalBallastDistance = totalDistance - totalLadenDistance;
  const totalPortDuration = totalDuration - totalSeaDuration;
  const tcCost =
    vessel?.tcRate != null ? msToD(totalDuration) * +vessel.tcRate : 0;
  return {
    massCapacityUsageByLadenLeg,
    tcCost,
    totalArrivalsInTime,
    totalBallastDistance,
    totalBunkerCost,
    totalDistance,
    totalDuration,
    totalLadenDistance,
    totalLoadedMass,
    totalLoadedOrders,
    totalLoadedVolume,
    totalPortDuration,
    totalSeaDuration,
    totalTonMiles,
    volumeCapacityUsageByLadenLeg,
    totalChannelCost,
  };
};

const getScenarioComparison = (timelines: EventTimeline[]): ComparisonData => {
  let totalArrivalsInTime = 0;
  let totalBallastDistance = 0;
  let totalBunkerCost = 0;
  let totalDistance = 0;
  let totalDuration = 0;
  let totalLadenDistance = 0;
  let totalLoadedMass = 0;
  let totalLoadedVolume = 0;
  let totalLoadedOrders = 0;
  let totalPortDuration = 0;
  let totalSeaDuration = 0;
  let totalTcCost = 0;
  let totalChannelCost = 0;
  let totalTonMiles = 0;
  const massCapacityUsageByLadenLeg: number[] = [];
  const volumeCapacityUsageByLadenLeg: number[] = [];

  const comparisonDataByTimeline = new Map<
    EventTimeline,
    TimelineComparisonData
  >();
  timelines.forEach((timeline) => {
    const d = getTimelineComparisonData(timeline);
    comparisonDataByTimeline.set(timeline, d);
    totalArrivalsInTime += d.totalArrivalsInTime;
    totalBallastDistance += d.totalBallastDistance;
    totalBunkerCost += d.totalBunkerCost;
    totalDistance += d.totalDistance;
    totalDuration += d.totalDuration;
    totalLadenDistance += d.totalLadenDistance;
    totalLoadedMass += d.totalLoadedMass;
    totalLoadedVolume += d.totalLoadedVolume;
    totalLoadedOrders += d.totalLoadedOrders;
    totalPortDuration += d.totalPortDuration;
    totalSeaDuration += d.totalSeaDuration;
    totalTcCost += d.tcCost;
    totalChannelCost += d.totalChannelCost;
    totalTonMiles += d.totalTonMiles;
    massCapacityUsageByLadenLeg.push(...d.massCapacityUsageByLadenLeg);
    volumeCapacityUsageByLadenLeg.push(...d.volumeCapacityUsageByLadenLeg);
  });

  const averageVesselFillRateMass =
    massCapacityUsageByLadenLeg.length > 0
      ? massCapacityUsageByLadenLeg.reduce((sum, rate) => sum + rate, 0) /
        massCapacityUsageByLadenLeg.length
      : 0;

  const averageVesselFillRateVolume =
    volumeCapacityUsageByLadenLeg.length > 0
      ? volumeCapacityUsageByLadenLeg.reduce((sum, rate) => sum + rate, 0) /
        volumeCapacityUsageByLadenLeg.length
      : 0;

  const ballastPercentage =
    totalDistance > 0 ? (totalBallastDistance / totalDistance) * 100 : 0;
  const arrivalHitRate =
    totalLoadedOrders > 0 ? (totalArrivalsInTime / totalLoadedOrders) * 100 : 0;
  const totalTcCostPerTonMile =
    totalTonMiles > 0 ? totalTcCost / totalTonMiles : 0;

  return {
    arrivalHitRate,
    averageVesselFillRateMass,
    averageVesselFillRateVolume,
    ballastPercentage,
    totalBallastDistance,
    totalBunkerCost,
    totalCargoMass: totalLoadedMass,
    totalCargoVolume: totalLoadedVolume,
    totalDuration,
    totalLadenDistance,
    totalPortDuration,
    totalSeaDuration,
    totalTcCost,
    totalTcCostPerTonMile,
    totalTonMiles,
    totalChannelCost,
    comparisonDataByTimeline,
    effectiveTonMiles: 0,
  };
};

type VesselFilter = { type: 'vessel' | 'attribute'; id: number };

export const useVesselFilters = (
  vessels: ScenarioComparisonProps['vessels'],
) => {
  const [initialVesselFilter, saveVesselFilter] = useLocalSetting(
    'scenarioComparisonVesselFilter',
  );

  const [vesselFilters, setVesselFilters] = useState<VesselFilter[]>(
    initialVesselFilter ?? [],
  );

  const hiddenVesselIds = useMemo(
    () =>
      new Set(
        vesselFilters.filter((f) => f.type === 'vessel').map((f) => f.id),
      ),
    [vesselFilters],
  );

  const selectedAttributeFilters = useMemo(
    () =>
      new Set(
        vesselFilters.filter((f) => f.type === 'attribute').map((f) => f.id),
      ),
    [vesselFilters],
  );

  const filteredVessels = useMemo(
    () =>
      vessels.filter((vessel) => {
        const vesselHidden = hiddenVesselIds.has(vessel.id);
        const attributes = vessel.vesselAttributesAssociationsByShipId.nodes
          .map((attr) => attr?.vesselAttributeId)
          .filter(isDefined);

        const attributeFilterMatches =
          selectedAttributeFilters.size === 0 ||
          attributes.some((attrId) => selectedAttributeFilters.has(attrId));

        return !vesselHidden && attributeFilterMatches;
      }),
    [vessels, hiddenVesselIds, selectedAttributeFilters],
  );

  const filteredVesselIds = useMemo(
    () => new Set(filteredVessels.map((v) => v.id)),
    [filteredVessels],
  );

  const inactiveVesselsCount = vessels.length - filteredVessels.length;

  const onVesselFilterChange = useCallback(
    (filter: VesselFilter, newValue: boolean) => {
      setVesselFilters((curVesselFilters) => {
        const existingFilterIndex = curVesselFilters.findIndex(
          (existingFilter) =>
            existingFilter.type === filter.type &&
            existingFilter.id === filter.id,
        );

        if (newValue && existingFilterIndex > -1) return curVesselFilters;

        const newVesselFilters = newValue
          ? [...curVesselFilters, filter]
          : [...curVesselFilters].filter((_f, i) => i !== existingFilterIndex);

        saveVesselFilter(newVesselFilters);
        return newVesselFilters;
      });
    },
    [setVesselFilters, saveVesselFilter],
  );

  const onVesselFilterToggleAll = useCallback(() => {
    if (hiddenVesselIds.size > 0) {
      setVesselFilters((curVesselFilters) => {
        const newFilters = curVesselFilters.filter((f) => f.type !== 'vessel');

        saveVesselFilter(newFilters);
        return newFilters;
      });
    } else {
      setVesselFilters((curVesselFilters) => {
        const newFilters = [
          ...curVesselFilters,
          ...vessels.map<{ type: 'vessel'; id: number }>((vessel) => ({
            type: 'vessel',
            id: vessel.id,
          })),
        ];

        saveVesselFilter(newFilters);
        return newFilters;
      });
    }
  }, [hiddenVesselIds, setVesselFilters, vessels, saveVesselFilter]);

  return {
    inactiveVesselsCount,
    setVesselFilters,
    hiddenVesselIds,
    filteredVessels,
    filteredVesselIds,
    onVesselFilterChange,
    onVesselFilterToggleAll,
    selectedAttributeFilters,
    saveVesselFilter,
  };
};

const hiddenColumnsAtom = persistedAtom({
  schema: zod.array(zod.string()),
  key: 'scenario_comparison_table_hidden_columns',
  defaultValue: [],
  getOnInit: true,
});

const useScenarioComparisonKPIRows = (): {
  allRows: ScenarioComparisonRow[];
  hiddenRows: string[];
  visibleRows: ScenarioComparisonRow[];
  onRowToggleChange: (
    rowKey: ScenarioComparisonRowKey,
    newValue: boolean,
  ) => void;
} => {
  const baseCurrency = useDefaultCurrency();

  const allRows: ScenarioComparisonRow[] = useMemo(
    () => getScenarioComparisonKPIRows(baseCurrency),
    [baseCurrency],
  );

  const [hiddenRows, saveHiddenRows] = useAtom(hiddenColumnsAtom);

  const visibleRows = useMemo(
    () => allRows.filter((row) => !hiddenRows.includes(row.key)),
    [hiddenRows, allRows],
  );

  const onRowToggleChange = useEvent(
    (rowKey: ScenarioComparisonRowKey, newValue: boolean) => {
      saveHiddenRows((curHiddenRows) => {
        if (newValue && curHiddenRows.includes(rowKey)) return curHiddenRows;

        return newValue
          ? [...curHiddenRows, rowKey]
          : curHiddenRows.filter((hiddenRowKey) => hiddenRowKey !== rowKey);
      });
    },
  );

  return { allRows, hiddenRows, visibleRows, onRowToggleChange };
};

/** Subset of useScenarioComparison, doesn't automatically apply filters */
export const useComparisonData = (
  scenarios: readonly ScenarioSchedule[],
  selectedScenarios: ClientId[],
  calculationConfig: CalculationConfig,
  getScenarioData: GetScenarioData,
  filteredVesselIds: Set<number>,
  dateFilter: { start: number; end: number | null },
  pnlData: PnlData,
) => {
  const currentUser = useCurrentUser();
  const euEtsCostEnabled = !!currentUser?.rootOrganization?.euEtsCosts;
  const includeFuelEuInCosts =
    !!currentUser?.rootOrganization?.configIncludeFuelEuInCosts;
  const client = useApolloClient();
  const { distances } = useDistances();
  const { bunkerPriceByGradeName } = useBunkerPrices();

  const { getTimelinePortCallCosts } = usePortCallCosts(pnlData);
  const calculateTimeDataPnl = useCalculateTimeDataPnl();

  const [subTimelinesByScenarioCId, setSubTimelinesByScenarioCId] = useState<
    Map<ClientId, EventTimeline[]>
  >(new Map());
  const [incomeByScenario, setIncomeByScenario] = useState<
    Map<
      ClientId,
      {
        totalIncome: number;
        totalEffectiveTonMiles: number;
        incomeByTimeline: Map<EventTimeline, number>;
      }
    >
  >(new Map());
  const [reletCostsByScenario, setReletCostsByScenario] = useState<
    Map<ClientId, FreightCosts>
  >(new Map());
  const [extraCostsByScenario, setExtraCostsByScenario] = useState<
    Map<
      ClientId,
      {
        totalExtraCosts: number;
        extraCostsByTimeline: Map<EventTimeline, number>;
      }
    >
  >(new Map());
  const [etsByScenario, setEtsByScenario] = useState<
    Map<ClientId, { eeoi: number | null; ets: number | null }>
  >(new Map());
  const [fuelEuByScenario, setfuelEuByScenario] = useState<
    Map<ClientId, number>
  >(new Map());

  const [loadingScenarios, setLoadingScenarios] = useState<Set<ClientId>>(
    new Set(),
  );
  const getScenarioPortCallCosts = useCallback(
    async (timelines: EventTimeline[]) => {
      const costsByTimeline = new Map<EventTimeline, number>();
      const portCostBreakDownByTimeline = new Map<
        EventTimeline,
        Map<string, number>
      >();
      let totalCosts = 0;

      const tcTimelines = timelines.filter((tl) => tl.type === 'tc');

      for (const timeline of tcTimelines) {
        const shipmentsByPortCallId = new DefaultMap<
          ClientId | null,
          Set<ClientId | null>
        >(() => new Set());
        timeline.voyageGroups.forEach((vg) => {
          vg.portCalls.forEach((pc) => {
            shipmentsByPortCallId.get(pc.id).add(null); // Ensure we don't split costs in comparison
          });
        });
        const timelineCosts = await getTimelinePortCallCosts(
          timeline,
          shipmentsByPortCallId,
          new Set(),
        );
        // create an empty map to store the costs for each port call
        let portCostBreakdown: Map<string, number> = new Map<string, number>();
        let costs = 0;
        if (timelineCosts?.portCallCosts) {
          portCostBreakdown = new Map<string, number>();
          for (const pcCosts of timelineCosts.portCallCosts.values()) {
            for (const cost of pcCosts) {
              if (cost?.type === 'PORT_CALL') {
                cost.params.forEach((param) => {
                  const key = `${param.name}`;
                  portCostBreakdown.set(
                    key,
                    (portCostBreakdown.get(key) ?? 0) +
                      (parseCurrencyString(param.value?.toString() ?? '0') ??
                        0),
                  );
                });
              }
              if (cost.value !== null) {
                costs += cost.value;
              }
            }
          }
          costsByTimeline.set(timeline, costs);
          portCostBreakDownByTimeline.set(timeline, portCostBreakdown);
          totalCosts += costs;
        }
      }
      return { totalCosts, costsByTimeline, portCostBreakDownByTimeline };
    },
    [getTimelinePortCallCosts],
  );

  const timelinesByScenario = useMemo(() => {
    const timelinesByScenario = new Map<ClientId, EventTimeline[]>();

    scenarios
      .filter(
        (scenario) =>
          /** Master scenario timelines are always calculated for change diffing */
          isMasterScenario(scenario) ||
          selectedScenarios.includes(scenario.clientId),
      )
      .forEach((scenario) => {
        const { timelines } = getScenarioData(scenario);
        timelinesByScenario.set(scenario.clientId, timelines);
      });

    return timelinesByScenario;
  }, [getScenarioData, scenarios, selectedScenarios]);

  const automaticFilterValues = useMemo(() => {
    const vesselIds: Set<number> = new Set();

    if (!selectedScenarios[0]) {
      return null;
    }

    const firstScenarioTimelines = timelinesByScenario.get(
      selectedScenarios[0],
    );
    if (!firstScenarioTimelines) {
      return null;
    }

    const otherScenariosEventTimeline = selectedScenarios
      .slice(1)
      .map((scenarioId) => timelinesByScenario.get(scenarioId))
      .filter(isDefined);

    const diffRangePerTimelineOutsideVoyage: [number, number][] = [];

    for (const eventTimeline of firstScenarioTimelines) {
      if (!eventTimeline.vesselId) {
        continue;
      }

      const otherScenarioEventTimeline = otherScenariosEventTimeline
        .map((timelines) =>
          timelines.find((oTl) => oTl.id === eventTimeline.id),
        )
        .filter(isDefined);

      const squashedEvents = [
        squashVoyageGroup(eventTimeline.voyageGroups),
        ...otherScenarioEventTimeline.map((oTl) =>
          squashVoyageGroup(oTl.voyageGroups),
        ),
      ];
      const diffTimerange = getEventsDifferenceTimerange(...squashedEvents);
      if (!diffTimerange) {
        continue;
      }

      vesselIds.add(eventTimeline.vesselId);

      const voyagesRangesForThisTimeline: VoyageRange[] = [];
      for (const events of squashedEvents) {
        voyagesRangesForThisTimeline.push(...getBallastToBallastRanges(events));
      }

      diffRangePerTimelineOutsideVoyage.push(
        fitRangeOutsideOfVoyages(
          diffTimerange[0],
          diffTimerange[1],
          voyagesRangesForThisTimeline,
        ),
      );
    }

    const start =
      diffRangePerTimelineOutsideVoyage.length > 0
        ? Math.min(
            ...diffRangePerTimelineOutsideVoyage.map((range) => range[0]),
          )
        : null;
    const end =
      diffRangePerTimelineOutsideVoyage.length > 0
        ? Math.max(
            ...diffRangePerTimelineOutsideVoyage.map((range) => range[1]),
          )
        : null;

    if (!start || !end) return null;

    return { vesselIds, start, end };
  }, [timelinesByScenario, selectedScenarios]);

  const [comparisonDataByScenario, setComparisonDataByScenario] = useState<
    Map<ClientId, ComparisonData>
  >(new Map());
  const [portCallCostsByScenario, setPortCallCostsByScenario] = useState<
    Map<
      ClientId,
      {
        totalCosts: number;
        costsByTimeline: Map<EventTimeline, number>;
        portCostBreakDownByTimeline: Map<EventTimeline, Map<string, number>>;
      }
    >
  >(new Map());

  const calculateSubTimeline = useMemo(
    () =>
      (timeline: EventTimeline): EventTimeline =>
        getSubTimeline(timeline, {
          timelineId: timeline.id,
          time: {
            range: {
              start: dateFilter.start,
              end: dateFilter.end ?? Infinity,
            },
            startingWithinRangeOnly: true,
          },
        }),
    [dateFilter],
  );

  const selectedSchedules = useAtomValue(selectedSchedulesAtom);
  const setScheduleMap = useSetAtom(scheduleMapAtom);

  useEffect(() => {
    let mounted = true;

    const orderById: Map<number, ScenarioOrder> = new Map();
    const subTimelines: Map<ClientId, EventTimeline[]> = new Map();
    const loadEventsByScenario: Map<
      ClientId,
      (TimeData<CargoEvent> & {
        event: CargoEvent & { type: CoreEventType.Load };
      })[]
    > = new Map();

    for (const [scenarioId, timelines] of timelinesByScenario.entries()) {
      timelines.map(calculateSubTimeline).forEach((t) => {
        switch (t.type) {
          case 'coa':
            setScheduleMap((cur) => {
              const newMap = new Map(cur);
              newMap.set(t.id, {
                id: t.coaId,
                type: 'coa',
                title: t.coa.name ?? 'Unknown COA',
              });
              return newMap;
            });
            break;
          case 'spot':
            setScheduleMap((cur) => {
              const newMap = new Map(cur);
              newMap.set(t.id, {
                type: 'spot',
                title: t.title ?? 'Unknown Spot',
              });
              return newMap;
            });
            break;
          case 'tc':
            setScheduleMap((cur) => {
              const newMap = new Map(cur);
              newMap.set(t.id, {
                type: 'tc',
                title: t.vessel?.name ?? 'Unknown Vessel',
              });
              return newMap;
            });
            break;
        }
      });

      const filteredVesselsAndSchedules = timelines.filter((timeline) => {
        const type = timeline.type;
        const vesselCondition =
          type !== 'tc' ||
          (type === 'tc' &&
            (timeline.vesselId === null ||
              filteredVesselIds.has(timeline.vesselId)));

        let scheduleCondition = false;
        if (type === 'tc') {
          scheduleCondition = selectedSchedules.tc;
        } else if (type === 'spot') {
          scheduleCondition = selectedSchedules.spots.has(timeline.id);
        } else if (type === 'coa') {
          scheduleCondition = selectedSchedules.coas.has(timeline.coaId);
        }

        return vesselCondition && scheduleCondition;
      });

      const focusedTimelines =
        filteredVesselsAndSchedules.map(calculateSubTimeline);
      subTimelines.set(scenarioId, focusedTimelines);

      const comparisonData = getScenarioComparison(focusedTimelines);

      setComparisonDataByScenario(
        (cur) => new Map(cur.set(scenarioId, comparisonData)),
      );

      setLoadingScenarios(
        (curLoadingScenarios) => new Set(curLoadingScenarios.add(scenarioId)),
      );

      getScenarioPortCallCosts(focusedTimelines)
        .then((totalPortCallCosts) => {
          if (mounted) {
            setPortCallCostsByScenario(
              (curPortCallCostsByScenario) =>
                new Map(
                  curPortCallCostsByScenario.set(
                    scenarioId,
                    totalPortCallCosts,
                  ),
                ),
            );
          }
        })
        .catch((err) => {
          console.error('Error fetching timeline costs', err);
        })
        .finally(() => {
          if (mounted) {
            setLoadingScenarios(
              (curLoadingScenarios) =>
                new Set(
                  [...curLoadingScenarios].filter((id) => id !== scenarioId),
                ),
            );
          }
        });

      loadEventsByScenario.set(scenarioId, []);
      for (const timeline of focusedTimelines) {
        for (const vg of timeline.voyageGroups) {
          for (const td of vg.events) {
            if (isLoadEventTimeData(td)) {
              loadEventsByScenario.get(scenarioId)?.push(td);
              const { order } = td.event;
              orderById.set(order.id, order);
            }
          }
        }
      }
    }

    setSubTimelinesByScenarioCId(subTimelines);

    client
      .query<FreightCostsQuery, FreightCostsQueryVariables>({
        query: FreightCostsDocument,
        variables: {
          orderIds: [
            ...new Set(
              [...loadEventsByScenario.values()].flatMap((loadEvents) =>
                loadEvents.map((e) => e.event.order.id),
              ),
            ),
          ],
        },
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        const nextReletCosts = new Map();

        for (const [scenarioCId, loadEvents] of loadEventsByScenario) {
          const freightCosts = calculateFreightCosts(
            loadEvents,
            data,
            pnlData.worldscaleFreightRates,
          );
          nextReletCosts.set(scenarioCId, freightCosts);
        }

        setReletCostsByScenario(nextReletCosts);
      });

    if (pnlData.incomingVoyageChartersById) {
      const incomingFreightByOrderId = getFreightsByOrderId(
        pnlData.incomingVoyageChartersById,
        orderById,
      );

      const getIncomeForLoadedAmount = incomeForLoadedAmountGetter(
        orderById,
        incomingFreightByOrderId,
        pnlData.incomingVoyageChartersById,
        calculationConfig.currencyRates,
        calculationConfig.defaultCurrency,
        bunkerPriceByGradeName,
        pnlData.worldscaleFreightRates,
      );

      const newIncomeByScenario: typeof incomeByScenario = new Map();
      const newExtraCostsByScenario: typeof extraCostsByScenario = new Map();
      const newEtsByScenario: typeof etsByScenario = new Map();
      const newFuelEuByScenario: typeof fuelEuByScenario = new Map();

      for (const [scenarioCId, timelines] of subTimelines) {
        let totalIncome = 0;
        let totalEffectiveTonMiles = 0;
        let totalExtraCosts = 0;
        const incomeByTimeline = new Map<EventTimeline, number>();
        const extraCostsByTimeline = new Map<EventTimeline, number>();

        let ets = 0;

        let fuelEu = 0;
        let totalCo2 = 0;

        for (const timeline of timelines) {
          let incomes = 0;
          let costs = 0;

          for (const vg of timeline.voyageGroups) {
            const {
              income,
              massDistance,
              loadedAmounts,
              loadedVoyageCharterIds,
            } = getTDIncomeAndMassDistance(
              vg.events,
              getIncomeForLoadedAmount,
              distances,
            );

            const extraCosts = getExtraCostsForLoadedAmount(
              loadedAmounts,
              loadedVoyageCharterIds,
              pnlData.incomingVoyageChartersById,
              orderById,
            );
            const extraCostsSum = sumBy(extraCosts, (cost) => cost.value ?? 0);

            incomes += income;
            totalEffectiveTonMiles += massDistance;
            costs += extraCostsSum;

            // Calculate pnl using new pnl calculator, only used for fuel eu for now
            let pnl: CalculatePnlResult | null = null;

            try {
              pnl = calculateTimeDataPnl(vg.events, {
                type: timeline.type === 'tc' ? 'vessel' : 'contract',
                vessel: timeline.vessel
                  ? {
                      grossTonnage: Number(timeline.vessel.grossTonnage || 0),
                      id: timeline.vessel.id,
                      name: timeline.vessel.name ?? 'N/A',
                      shortName: timeline.vessel.shortName ?? null,
                    }
                  : null,
              });
            } catch (err) {
              console.error('Error calculating new pnl in comparison', err);
            }

            if (pnl) {
              for (const cost of pnl.costs) {
                switch (cost.category) {
                  case 'eu_ets_cost': {
                    ets += cost.amount;
                    break;
                  }
                  case 'fuel_eu_cost': {
                    fuelEu += cost.amount;
                    break;
                  }
                }
              }
              totalCo2 += sumBy(
                pnl.extras.co2Processor.co2Emissions,
                (e) => e.emittedCo2Tonnes,
              );
            }
          }

          incomeByTimeline.set(timeline, incomes);
          totalIncome += incomes;
          extraCostsByTimeline.set(timeline, costs);
          totalExtraCosts += costs;
        }

        newIncomeByScenario.set(scenarioCId, {
          totalIncome,
          totalEffectiveTonMiles,
          incomeByTimeline,
        });

        newExtraCostsByScenario.set(scenarioCId, {
          totalExtraCosts,
          extraCostsByTimeline,
        });

        newEtsByScenario.set(scenarioCId, {
          eeoi:
            totalEffectiveTonMiles > 0
              ? (1_000_000 * totalCo2) / totalEffectiveTonMiles
              : 0,
          ets,
        });
        newFuelEuByScenario.set(scenarioCId, fuelEu);
      }

      setIncomeByScenario((curIncomeByScenario) => {
        const nextIncomeByScenario = new Map(curIncomeByScenario);

        newIncomeByScenario.forEach((income, scenarioId) => {
          nextIncomeByScenario.set(scenarioId, income);
        });

        return nextIncomeByScenario;
      });

      setExtraCostsByScenario((curExtraCostsByScenario) => {
        const nextExtraCostsByScenario = new Map(curExtraCostsByScenario);

        newExtraCostsByScenario.forEach((extraCosts, scenarioId) => {
          nextExtraCostsByScenario.set(scenarioId, extraCosts);
        });

        return nextExtraCostsByScenario;
      });

      setEtsByScenario((curEtsByScenario) => {
        const nextEtsByScenario = new Map(curEtsByScenario);

        newEtsByScenario.forEach((ets, scenarioId) => {
          nextEtsByScenario.set(scenarioId, ets);
        });

        return nextEtsByScenario;
      });

      setfuelEuByScenario((curFuelEuByScenario) => {
        const nextFuelEuByScenario = new Map(curFuelEuByScenario);

        newFuelEuByScenario.forEach((fuelEu, scenarioId) => {
          nextFuelEuByScenario.set(scenarioId, fuelEu);
        });

        return nextFuelEuByScenario;
      });
    }

    return () => {
      setLoadingScenarios(new Set());
      mounted = false;
    };
  }, [
    client,
    distances,
    selectedSchedules,
    timelinesByScenario,
    getScenarioPortCallCosts,

    bunkerPriceByGradeName,
    calculateSubTimeline,
    setScheduleMap,
    calculationConfig.currencyRates,
    calculationConfig.defaultCurrency,
    pnlData.incomingVoyageChartersById,
    pnlData.outgoingVoyageChartersById,
    pnlData.indexedCoaPricesByCoaId,
    pnlData.worldscaleFreightRates,
    filteredVesselIds,
    includeFuelEuInCosts,
    calculateTimeDataPnl,
  ]);

  const changedOrdersByScenario = useMemo<
    Map<
      ClientId,
      { assignedOrderIds: Set<number>; unassignedOrderIds: Set<number> }
    >
  >(() => {
    const masterScenario = scenarios.find((s) => isMasterScenario(s));

    if (!masterScenario) return new Map();

    const masterScenarioOrders = getAssignedOrderIds(
      masterScenario,
      filteredVesselIds,
    );

    return new Map(
      scenarios
        .filter((s) => !isMasterScenario(s))
        .map((scenario) => {
          const scenarioOrders = getAssignedOrderIds(
            scenario,
            filteredVesselIds,
          );

          const assignedOrderIds = setDifference(scenarioOrders, [
            masterScenarioOrders,
          ]);

          const unassignedOrderIds = setDifference(masterScenarioOrders, [
            scenarioOrders,
          ]);

          return [
            scenario.clientId,
            {
              assignedOrderIds,
              unassignedOrderIds,
            },
          ];
        }),
    );
  }, [scenarios, filteredVesselIds]);

  const comparisonData = useMemo(
    () =>
      new Map(
        scenarios.map(({ clientId }) => {
          const scenarioCD = comparisonDataByScenario.get(clientId);
          const income = incomeByScenario.get(clientId);
          const subTimelines = subTimelinesByScenarioCId.get(clientId);
          const reletCosts = reletCostsByScenario.get(clientId);
          const extraCosts = extraCostsByScenario.get(clientId);
          const ets = etsByScenario.get(clientId);
          const fuelEu = fuelEuByScenario.get(clientId);

          const totalPortCallCosts =
            portCallCostsByScenario.get(clientId) ?? null;
          // sum of all port cost breakdown by keys
          const sumOfPortCostBreakdown = new Map<string, number>();
          totalPortCallCosts?.portCostBreakDownByTimeline.forEach(
            (portCostBreakdown) => {
              portCostBreakdown.forEach((value, key) => {
                sumOfPortCostBreakdown.set(
                  key,
                  (sumOfPortCostBreakdown.get(key) ?? 0) + value,
                );
              });
            },
          );
          // Total TCE is currently removed from KPI options and not in use,
          // but might be needed in the future
          let totalTCE = 0;
          const euEts = euEtsCostEnabled ? (ets?.ets ?? 0) : 0;
          const includedFuelEuCosts = includeFuelEuInCosts ? (fuelEu ?? 0) : 0;
          for (const timeline of subTimelines ?? []) {
            const totalIncome = income?.incomeByTimeline.get(timeline) ?? 0;
            const portCallCost =
              totalPortCallCosts?.costsByTimeline.get(timeline) ?? 0;
            const timelineCD =
              scenarioCD?.comparisonDataByTimeline?.get(timeline);
            const timelineExtraCosts =
              extraCosts?.extraCostsByTimeline.get(timeline) ?? 0;

            const totalCost =
              portCallCost +
              (timelineCD?.totalBunkerCost ?? 0) +
              (timelineCD?.totalChannelCost ?? 0) +
              timelineExtraCosts +
              euEts +
              includedFuelEuCosts;

            const days = msToD(timelineCD?.totalDuration ?? 0);
            const tce = calculateTce(totalIncome, totalCost, days);
            totalTCE += tce;
          }

          const totalReletCost =
            reletCosts?.reduce((acc, curr) => acc + curr.value, 0) ?? 0;

          const totalVoyageCost =
            (scenarioCD
              ? scenarioCD.totalBunkerCost + scenarioCD.totalChannelCost
              : 0) +
            (totalPortCallCosts?.totalCosts ?? 0) +
            totalReletCost +
            (extraCosts?.totalExtraCosts ?? 0) +
            euEts +
            includedFuelEuCosts;

          const totalCost =
            totalVoyageCost + (scenarioCD ? scenarioCD.totalTcCost : 0);

          const effectiveTonMiles = income?.totalEffectiveTonMiles ?? 0;

          const totalCostPerTonMile =
            effectiveTonMiles > 0 ? totalCost / effectiveTonMiles : 0;

          const totalTCCostPerTonMile =
            effectiveTonMiles > 0
              ? (scenarioCD?.totalTcCost ?? 0) / effectiveTonMiles
              : 0;

          const changedOrders = changedOrdersByScenario.get(clientId);

          const days = msToD(scenarioCD?.totalDuration ?? 0);
          const totalIncome = income?.totalIncome ?? 0;
          const averageTCE =
            days === 0
              ? 0
              : (totalIncome -
                  ((totalPortCallCosts?.totalCosts ?? 0) +
                    (scenarioCD?.totalBunkerCost ?? 0) +
                    (scenarioCD?.totalChannelCost ?? 0) +
                    (extraCosts?.totalExtraCosts ?? 0) +
                    euEts +
                    includedFuelEuCosts)) /
                days;

          const data = scenarioCD
            ? {
                ...scenarioCD,
                totalIncome,
                totalTCE,
                averageTCE,
                totalCost,
                totalCostPerTonMile,
                totalTCCostPerTonMile,
                totalPortCallCosts,
                totalPortCostBreakdown: sumOfPortCostBreakdown,
                totalReletCost,
                totalVoyageCost,
                effectiveTonMiles,
                assignedOrderIds: changedOrders?.assignedOrderIds ?? new Set(),
                unassignedOrderIds:
                  changedOrders?.unassignedOrderIds ?? new Set(),
                totalEeoi: ets?.eeoi ?? null,
                totalEts: ets?.ets ?? null,
                totalFuelEu: fuelEu ?? null,
              }
            : null;

          return [clientId, data];
        }),
      ),
    [
      scenarios,
      comparisonDataByScenario,
      incomeByScenario,
      subTimelinesByScenarioCId,
      reletCostsByScenario,
      extraCostsByScenario,
      etsByScenario,
      fuelEuByScenario,
      portCallCostsByScenario,
      euEtsCostEnabled,
      includeFuelEuInCosts,
      changedOrdersByScenario,
    ],
  );

  return {
    comparisonData,
    automaticFilterValues,
    loadingScenarios,
    incomeByScenario,
    reletCostsByScenario,
    subTimelinesByScenarioCId,
    timelinesByScenario,
    calculateSubTimeline,
  };
};

type UseScenarioComparison = typeof useScenarioComparison;

export const useScenarioComparison = (
  scenarios: readonly ScenarioSchedule[],
  vessels: ScenarioComparisonProps['vessels'],
  getScenarioData: ScenarioComparisonProps['getScenarioData'],
  calculationConfig: ScenarioComparisonProps['calculationConfig'],
  pnlData: PnlData,
  selectedScenarioId: ClientId,
  selectedBaseScenario: ClientId | undefined,
) => {
  const [isAutomaticFilteringEnabled, setIsAutomaticFilteringEnabled] =
    useLocalSetting('scenarioComparisonAutomaticFiltersEnabled', false);
  const [savedSelectedScenarios, saveSelectedScenarios] = useLocalSetting(
    'scenarioComparisonSelectedScenarios',
  );
  const [savedDateFilter, saveDateFilter] = useLocalSetting(
    'scenarioComparisonDateFilter',
  );

  const scenarioClientIds = new Set(
    scenarios
      .filter((scenario) => !scenario.deletedAt)
      .map((scenario) => scenario.clientId),
  );
  const [selectedScenarios, setSelectedScenarios] = useState<ClientId[]>(
    isAutomaticFilteringEnabled
      ? [
          MASTER_SCENARIO_CLIENTID,
          ...(selectedScenarioId !== MASTER_SCENARIO_CLIENTID
            ? [selectedScenarioId]
            : []),
        ]
      : (savedSelectedScenarios?.filter(
          (clientId: ClientId): clientId is ClientId =>
            scenarioClientIds.has(clientId as ClientId),
        ) ?? []),
  );

  const [dateFilter, setDateFilter] = useState<{
    start: number;
    end: number | null;
  }>({
    start:
      savedDateFilter?.start ?? moment().tz('UTC').startOf('day').valueOf(),
    end:
      savedDateFilter?.end ??
      moment().tz('UTC').endOf('day').add(1, 'month').valueOf(),
  });

  const useVesselFiltersResult = useVesselFilters(vessels);

  const { setVesselFilters, filteredVesselIds } = useVesselFiltersResult;

  const addScenarioToComparison = useCallback(
    (scenarioId: ClientId) => {
      setSelectedScenarios((curSelectedScenarios) => {
        const newSelectedScenarios = curSelectedScenarios.includes(scenarioId)
          ? curSelectedScenarios
          : [...curSelectedScenarios, scenarioId];

        saveSelectedScenarios(newSelectedScenarios);
        return newSelectedScenarios;
      });
    },
    [saveSelectedScenarios],
  );

  const removeScenarioFromComparison = useCallback(
    (scenarioId: ClientId) => {
      setSelectedScenarios((curSelectedScenarios) => {
        const newSelectedScenarios = curSelectedScenarios.filter(
          (id) => id !== scenarioId,
        );

        saveSelectedScenarios(newSelectedScenarios);
        return newSelectedScenarios;
      });
    },
    [saveSelectedScenarios],
  );

  const [scenariosToCalculate, setScenariosToCalculate] =
    useState<ClientId[]>(selectedScenarios);

  useEffect(() => {
    if (
      selectedBaseScenario &&
      !selectedScenarios.includes(selectedBaseScenario)
    ) {
      setScenariosToCalculate([
        ...selectedScenarios,
        selectedBaseScenario as ClientId,
      ]);
    } else {
      setScenariosToCalculate([...selectedScenarios]);
    }
  }, [selectedBaseScenario, selectedScenarios]);

  const {
    comparisonData,
    automaticFilterValues,
    loadingScenarios,
    incomeByScenario,
    reletCostsByScenario,
    subTimelinesByScenarioCId,
    timelinesByScenario,
    calculateSubTimeline,
  } = useComparisonData(
    scenarios,
    scenariosToCalculate,
    calculationConfig,
    getScenarioData,
    filteredVesselIds,
    dateFilter,
    pnlData,
  );

  useEffect(() => {
    /** Updates filters if automatic filtering is enabled  */
    if (isAutomaticFilteringEnabled && automaticFilterValues) {
      setVesselFilters((curVesselFilters) => {
        const newVesselFilters: VesselFilter[] = vessels
          .filter((v) => !automaticFilterValues.vesselIds.has(v.id))
          .map((v) => ({
            type: 'vessel',
            id: v.id,
          }));

        if (
          newVesselFilters.length === curVesselFilters.length &&
          newVesselFilters.every((newFilter) =>
            curVesselFilters.find(
              (curFilter) =>
                curFilter.type === newFilter.type &&
                curFilter.id === newFilter.id,
            ),
          )
        ) {
          return curVesselFilters;
        }

        return newVesselFilters;
      });

      setDateFilter((curDateFilter) => {
        if (
          curDateFilter.start === automaticFilterValues.start &&
          curDateFilter.end === automaticFilterValues.end
        ) {
          return curDateFilter;
        }

        return {
          start: automaticFilterValues.start,
          end: automaticFilterValues.end,
        };
      });
    }
  }, [
    automaticFilterValues,
    isAutomaticFilteringEnabled,
    setVesselFilters,
    vessels,
  ]);

  const setDateRange = useCallback(
    (newDateFilter: { start: number; end: number }) => {
      setIsAutomaticFilteringEnabled(false);
      saveDateFilter(newDateFilter);
      setDateFilter(newDateFilter);
    },
    [setIsAutomaticFilteringEnabled, saveDateFilter],
  );

  return {
    addScenarioToComparison,
    dateRange: dateFilter,
    loadingScenarios,
    comparisonData,
    removeScenarioFromComparison,
    selectedScenarios,
    setDateRange,
    useVesselFiltersResult,
    isAutomaticFilteringEnabled,
    setIsAutomaticFilteringEnabled,
    automaticFilterValues,
    incomeByScenario,
    reletCostsByScenario,
    subTimelinesByScenarioCId,
    timelinesByScenario,
    calculateSubTimeline,
  };
};

type ValueWithDelta = {
  value: number;
  delta: number | null;
};

type NullableValueWithDelta = {
  value: number | null;
  delta: number | null;
};

export interface ScenarioComparisonColumn {
  scenarioId: ClientId;
  scenarioName: string | null;
  arrivalHitRate: ValueWithDelta;
  assignedOrders: ScenarioOrderChange[];
  averageVesselFillRateMass: ValueWithDelta;
  averageVesselFillRateVolume: ValueWithDelta;
  ballastPercentage: ValueWithDelta;
  totalBallastDistance: ValueWithDelta;
  totalBunkerCost: ValueWithDelta;
  totalCargoMass: ValueWithDelta;
  totalCargoVolume: ValueWithDelta;
  totalCost: ValueWithDelta;
  totalCostPerTonMile: ValueWithDelta;
  totalDays: ValueWithDelta;
  totalEeoi: NullableValueWithDelta;
  totalEts: NullableValueWithDelta;
  totalFuelEu: NullableValueWithDelta;
  totalLadenDistance: ValueWithDelta;
  fairwayDuesPortCost: NullableValueWithDelta;
  harborDuesPortCost: NullableValueWithDelta;
  pilotagePortCost: NullableValueWithDelta;
  mooringUnmooringPortCost: NullableValueWithDelta;
  totalPortCallCosts: NullableValueWithDelta;
  totalProfit: ValueWithDelta;
  totalReletCost: ValueWithDelta;
  totalPortDays: ValueWithDelta;
  totalSeaDays: ValueWithDelta;
  totalChannelCost: ValueWithDelta;
  totalTcCost: ValueWithDelta;
  totalTcCostPerTonMile: ValueWithDelta;
  effectiveTonMiles: ValueWithDelta;
  totalTonMiles: ValueWithDelta;
  totalIncome: ValueWithDelta;
  totalTCE: number;
  totalVoyageCost: ValueWithDelta;
  totalVoyageProfit: ValueWithDelta;
  averageTCE: ValueWithDelta;
  unassignedOrders: ScenarioOrderChange[];
}

type ScenarioComparisonRowKey =
  | keyof Omit<
      ScenarioComparisonColumn,
      'totalDays' | 'totalSeaDays' | 'totalPortDays'
    >
  | 'days';

interface ScenarioComparisonRow {
  goodValue?: 'high' | 'low';
  header: string;
  key: ScenarioComparisonRowKey;
  tooltip: string;
  value: ({
    col,
    loading,
  }: {
    col: ScenarioComparisonColumn;
    loading: boolean;
  }) => React.ReactNode;
}

const KpiTooltip = React.memo(
  ({ tooltip }: ScenarioComparisonRow): JSX.Element => {
    return <Text>{tooltip}</Text>;
  },
  isEqual,
);

export function FilterVessels({
  vessels,
  useVesselFiltersResult: {
    inactiveVesselsCount,
    hiddenVesselIds,
    onVesselFilterChange,
    onVesselFilterToggleAll,
    selectedAttributeFilters,
    saveVesselFilter,
    setVesselFilters,
    filteredVessels,
  },
  onChange,
}: {
  vessels: ScenarioComparisonProps['vessels'];
  useVesselFiltersResult: ReturnType<typeof useVesselFilters>;
  onChange?: () => void;
}): JSX.Element {
  const { data } = useAllVesselAttributesQuery();
  const [searchKeyword, setSearchKeyword] = useState('');
  const vesselAttributes: VesselAttributes[] =
    data?.allVesselAttributes?.nodes.filter(isDefined) ?? [];

  const onVesselFilterReset = useEvent(() => {
    setVesselFilters([]);
    saveVesselFilter([]);
    onChange?.();
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  };

  const displayedVessels = vessels.filter((vessel) =>
    vessel.name?.toLowerCase().includes(searchKeyword.toLowerCase()),
  );

  const displayedVesselAttributes = vesselAttributes.filter((attrubute) =>
    attrubute.name?.toLowerCase().includes(searchKeyword.toLowerCase()),
  );

  return (
    <>
      <DropdownButton
        label={
          <Text>
            Filter vessels{' '}
            {inactiveVesselsCount > 0 && (
              <Badge isRounded variant="primary">
                {filteredVessels.length}
              </Badge>
            )}
          </Text>
        }
        variant="secondary"
        closeOnContentClick={false}
        width="100%"
      >
        <Menu hasShadow>
          <Box
            display="flex"
            flexDirection="column"
            width="100%"
            maxWidth="400px"
            maxHeight="50vh"
            overflowY="auto"
          >
            <Box display="flex" position="relative">
              <Input
                name="Search"
                value={searchKeyword}
                onChange={handleSearchChange}
                placeholder="Search by name..."
              />
              {searchKeyword && (
                <DeleteButton
                  position="absolute"
                  right={8}
                  bottom={2}
                  marginBottom={1}
                  onPress={() => setSearchKeyword('')}
                />
              )}
            </Box>
            <Box p={2} mb={1}>
              <Text fontSize={1} fontWeight="bold" color="neutral.gray_340">
                Filter by vessel
              </Text>
            </Box>
            <Box
              as="label"
              display={searchKeyword.length > 0 ? 'none' : 'flex'}
              alignItems="center"
              mb={1}
            >
              <input
                type="checkbox"
                checked={hiddenVesselIds.size === 0}
                onChange={() => {
                  onVesselFilterToggleAll();
                  onChange?.();
                }}
              />
              <Text ml={2} isUnselectable>
                Toggle all
              </Text>
            </Box>
            <Box>
              {displayedVessels.map((vessel) => (
                <Box
                  data-testid={`vessel-${vessel.name}`}
                  as="label"
                  key={`vessel-${vessel.id}`}
                  display="flex"
                  style={{ float: 'left' }}
                  marginRight={3}
                  alignItems="center"
                  mb={1}
                >
                  <input
                    type="checkbox"
                    checked={!hiddenVesselIds.has(vessel.id)}
                    onChange={(e) => {
                      onVesselFilterChange(
                        { type: 'vessel', id: vessel.id },
                        !e.target.checked,
                      );
                      onChange?.();
                    }}
                  />
                  <Text ml={2} isUnselectable>
                    {vessel.name}
                  </Text>
                </Box>
              ))}
            </Box>
            <>
              <Box p={2} my={1}>
                <Text fontSize={1} fontWeight="bold" color="neutral.gray_340">
                  Filter by attributes
                </Text>
              </Box>
              <Box>
                {displayedVesselAttributes.map((vesselAttribute) => (
                  <Box
                    as="label"
                    key={`vessel-${vesselAttribute.id}`}
                    display="flex"
                    style={{ float: 'left' }}
                    marginRight={3}
                    alignItems="center"
                    mb={1}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAttributeFilters.has(vesselAttribute.id)}
                      onChange={(e) => {
                        onVesselFilterChange(
                          {
                            type: 'attribute',
                            id: vesselAttribute.id,
                          },
                          e.target.checked,
                        );
                        onChange?.();
                      }}
                    />
                    <Text ml={2} isUnselectable>
                      {vesselAttribute.name}
                    </Text>
                  </Box>
                ))}
              </Box>
            </>
          </Box>
          <Box p={2} mt={3}>
            <Button variant="secondary" onPress={onVesselFilterReset}>
              Reset
            </Button>
          </Box>
        </Menu>
      </DropdownButton>
    </>
  );
}

type VesselAttributes = NonNullable<
  NonNullable<AllVesselAttributesQuery['allVesselAttributes']>['nodes'][number]
>;

const getScenarioComparisonKPIRows = (
  baseCurrency: Currency,
): ScenarioComparisonRow[] => {
  const showPortCostBreakdown = hasFeature('s5_integration');
  const portCostBreakdownElements: ScenarioComparisonRow[] = [
    {
      goodValue: 'low',
      key: 'fairwayDuesPortCost',
      header: 'Fairway Dues Port Cost',
      tooltip: 'Total Fairway Dues port call costs',
      value: ({ col, loading }) =>
        loading ? (
          <LoadingSpinner />
        ) : col.fairwayDuesPortCost.value != null ? (
          formatValue({
            value: col.fairwayDuesPortCost.value ?? 0,
            delta: col.fairwayDuesPortCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          })
        ) : null,
    },
    {
      goodValue: 'low',
      key: 'pilotagePortCost',
      header: 'Pilotage Dues Port Cost',
      tooltip: 'Total Pilotage Dues port call costs',
      value: ({ col, loading }) =>
        loading ? (
          <LoadingSpinner />
        ) : col.pilotagePortCost.value != null ? (
          formatValue({
            value: col.pilotagePortCost.value ?? 0,
            delta: col.pilotagePortCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          })
        ) : null,
    },
    {
      goodValue: 'low',
      key: 'harborDuesPortCost',
      header: 'Harbor Dues Port Cost',
      tooltip: 'Total Harbor Dues port call costs',
      value: ({ col, loading }) =>
        loading ? (
          <LoadingSpinner />
        ) : col.harborDuesPortCost.value != null ? (
          formatValue({
            value: col.harborDuesPortCost.value ?? 0,
            delta: col.harborDuesPortCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          })
        ) : null,
    },
    {
      goodValue: 'low',
      key: 'mooringUnmooringPortCost',
      header: 'Mooring & Unmooring Dues Port Cost',
      tooltip: 'Mooring and Unmooring Dues port call costs',
      value: ({ col, loading }) =>
        loading ? (
          <LoadingSpinner />
        ) : col.mooringUnmooringPortCost.value != null ? (
          formatValue({
            value: col.mooringUnmooringPortCost.value ?? 0,
            delta: col.mooringUnmooringPortCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          })
        ) : null,
    },
  ];

  const formatValue = ({
    value,
    delta,
    digits,
    isCurrency,
    unit,
    currency,
  }: {
    value: number;
    delta: number | null;
    digits: number;
    isCurrency: boolean;
    unit?: string;
    currency?: Currency;
  }) => {
    const formattedValue = isCurrency
      ? formatCurrency(value, digits, currency)
      : `${formatNumber(value, digits)} ${unit}`;
    if (delta) {
      const deltaSign = delta > 0 ? '+' : '';
      return `${formattedValue} (${deltaSign}${formatNumber(delta, digits)})`;
    } else return formattedValue;
  };

  const getPositiveSign = (input?: number | null) =>
    input && input > 0 ? '+' : '';

  const scenarioComparisonRows = [
    ...([
      {
        key: 'totalCargoMass',
        header: 'Total cargo mass',
        tooltip: 'Total cargo amount by mass',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalCargoMass.value,
            delta: col.totalCargoMass.delta,
            digits: 0,
            isCurrency: false,
            unit: 'mts',
          });

          return formattedValue;
        },
      },
      {
        key: 'totalCargoVolume',
        header: 'Total cargo volume',
        tooltip: 'Total cargo amount by volume',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalCargoVolume.value,
            delta: col.totalCargoVolume.delta,
            digits: 0,
            isCurrency: false,
            unit: 'cbm',
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'high',
        key: 'averageVesselFillRateMass',
        header: 'Avg. vessel fill rate (mass)',
        tooltip:
          'Average of vessel capacity utilization per voyage based on mass loaded / mass capacity',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.averageVesselFillRateMass.value,
            delta: col.averageVesselFillRateMass.delta,
            digits: 2,
            isCurrency: false,
            unit: '%',
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'high',
        key: 'averageVesselFillRateVolume',
        header: 'Avg. vessel fill rate (volume)',
        tooltip:
          'Average of vessel capacity utilization per voyage based on volume loaded / volume capacity',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.averageVesselFillRateVolume.value,
            delta: col.averageVesselFillRateVolume.delta,
            digits: 2,
            isCurrency: false,
            unit: '%',
          });
          return formattedValue;
        },
      },
      {
        key: 'totalTonMiles',
        header: 'Total ton miles',
        tooltip:
          'Total cargo amount by mass in tons multiplied by total laden distance in nautical miles (calculated per parcel and per leg)',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalTonMiles.value,
            delta: col.totalTonMiles.delta,
            digits: 0,
            isCurrency: false,
            unit: 'TNM',
          });
          return formattedValue;
        },
      },
      {
        key: 'totalBallastDistance',
        header: 'Total ballast distance',
        tooltip:
          'Total nautical miles the vessels have travelled in ballast state',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalBallastDistance.value,
            delta: col.totalBallastDistance.delta,
            digits: 0,
            isCurrency: false,
            unit: 'NM',
          });
          return formattedValue;
        },
      },
      {
        key: 'totalLadenDistance',
        header: 'Total laden distance',
        tooltip:
          'Total nautical miles the vessels have travelled in laden state',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalLadenDistance.value,
            delta: col.totalLadenDistance.delta,
            digits: 0,
            isCurrency: false,
            unit: 'NM',
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'ballastPercentage',
        header: 'Ballast %',
        tooltip: 'Percentage of ballast miles compared to total nautical miles',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.ballastPercentage.value,
            delta: col.ballastPercentage.delta,
            digits: 2,
            isCurrency: false,
            unit: '%',
          });
          return formattedValue;
        },
      },
      {
        key: 'days',
        header: 'Total days / port days / sea days',
        tooltip:
          'Total voyage time / Total days vessels at port / Total days vessels at sea',
        value: ({ col }) => {
          const formattedValue = `${formatNumber(col.totalDays.value, 0)} / ${formatNumber(
            col.totalPortDays.value,
            0,
          )} / ${formatNumber(col.totalSeaDays.value, 0)}`;

          if (
            col.totalDays.delta ||
            col.totalPortDays.delta ||
            col.totalSeaDays.delta
          ) {
            const daysDeltaSign = getPositiveSign(col.totalDays.delta);
            const portDaysDeltaSign = getPositiveSign(col.totalPortDays.delta);
            const seaDaysDeltaSign = getPositiveSign(col.totalSeaDays.delta);
            return `${formattedValue} (${daysDeltaSign}${formatNumber(col.totalDays.delta, 0)} /
            ${portDaysDeltaSign}${formatNumber(col.totalPortDays.delta, 0)} /
            ${seaDaysDeltaSign}${formatNumber(col.totalSeaDays.delta, 0)})`;
          }

          return formattedValue;
        },
      },
      {
        goodValue: 'high',
        key: 'arrivalHitRate',
        header: 'Arrival hit rate',
        tooltip:
          'Amount of current vessel ETA within earliest and latest loading days divided by total voyages',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.arrivalHitRate.value,
            delta: col.arrivalHitRate.delta,
            digits: 2,
            isCurrency: false,
            unit: '%',
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalChannelCost',
        header: 'Total Channel costs',
        tooltip: 'Total Channel costs for the performance of the scenario',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalChannelCost.value,
            delta: col.totalChannelCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalTcCost',
        header: 'Total TC costs',
        tooltip:
          'Total TC costs for the performance of the scenario (based on days used)',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalTcCost.value,
            delta: col.totalTcCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalTcCostPerTonMile',
        header: 'Total TC costs per ton mile',
        tooltip: 'Total TC costs divided by effective ton miles',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalTcCostPerTonMile.value,
            delta: col.totalTcCostPerTonMile.delta,
            digits: 4,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'high',
        key: 'effectiveTonMiles',
        header: 'Effective ton miles',
        tooltip:
          'Effective ton miles = SUM(Cargo mass in mts * shortest distance between load and discharge ports in nm)',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.effectiveTonMiles.value,
            delta: col.effectiveTonMiles.delta,
            digits: 0,
            isCurrency: false,
            unit: 'TNM',
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalBunkerCost',
        header: 'Total bunker cost',
        tooltip: 'Total bunker cost for the performance of the scenario',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalBunkerCost.value,
            delta: col.totalBunkerCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
    ] as ScenarioComparisonRow[]),
    ...(showPortCostBreakdown ? portCostBreakdownElements : []),
    ...([
      {
        goodValue: 'low',
        key: 'totalPortCallCosts',
        header: 'Total port call costs',
        tooltip: 'Total port call costs for the performance of the scenario',
        value: ({ col, loading }) =>
          loading ? (
            <LoadingSpinner />
          ) : col.totalPortCallCosts.value !== null ? (
            formatValue({
              value: col.totalPortCallCosts.value,
              delta: col.totalPortCallCosts.delta,
              digits: 0,
              isCurrency: true,
              currency: baseCurrency,
            })
          ) : null,
      },
      {
        goodValue: 'low',
        key: 'totalReletCost',
        header: 'Total freight costs',
        tooltip: 'Total freight costs',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalReletCost.value,
            delta: col.totalReletCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalVoyageCost',
        header: 'Total voyage cost',
        tooltip:
          'Total costs (Bunker + Port call costs + Relet costs) without TC',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalVoyageCost.value,
            delta: col.totalVoyageCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalCost',
        header: 'Total cost',
        tooltip: 'Total costs (TC + Bunker + Port call costs + Relet costs)',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalCost.value,
            delta: col.totalCost.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalCostPerTonMile',
        header: 'Total cost per ton mile',
        tooltip:
          'Total costs (TC + Bunker + Port call costs + Relet costs) divided by effective ton miles',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalCostPerTonMile.value,
            delta: col.totalCostPerTonMile.delta,
            digits: 4,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'high',
        key: 'totalIncome',
        header: 'Total income',
        tooltip: 'Total income',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalIncome.value,
            delta: col.totalIncome.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'high',
        key: 'averageTCE',
        header: 'Average TCE',
        tooltip: 'Average TCE',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.averageTCE.value,
            delta: col.averageTCE.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'high',
        key: 'totalVoyageProfit',
        header: 'Total voyage profit',
        tooltip: 'Total voyage profit (Total income - Total voyage cost)',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalVoyageProfit.value,
            delta: col.totalVoyageProfit.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'high',
        key: 'totalProfit',
        header: 'Total profit',
        tooltip: 'Total profit (Total income - Total cost)',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalProfit.value,
            delta: col.totalProfit.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalEeoi',
        header: 'EEOI',
        tooltip: 'EEOI (grams per ton-mile)',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalEeoi.value ?? 0,
            delta: col.totalEeoi.delta,
            digits: 2,
            isCurrency: false,
            unit: 'g/TNM',
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalEts',
        header: 'EU ETS',
        tooltip: 'EU ETS Cost',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalEts.value ?? 0,
            delta: col.totalEts.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        goodValue: 'low',
        key: 'totalFuelEu',
        header: 'Fuel EU',
        tooltip: 'Fuel EU Cost',
        value: ({ col }) => {
          const formattedValue = formatValue({
            value: col.totalFuelEu.value ?? 0,
            delta: col.totalFuelEu.delta,
            digits: 0,
            isCurrency: true,
            currency: baseCurrency,
          });
          return formattedValue;
        },
      },
      {
        key: 'assignedOrders',
        header: 'Assigned orders',
        tooltip:
          'Additional assigned orders on scenario compared to Committed scenario',
        value: ({ col }) => (
          <ScenarioOrderTable orderChanges={col.assignedOrders} />
        ),
      },
      {
        key: 'unassignedOrders',
        header: 'Unassigned orders',
        tooltip: 'Removed orders on scenario compared to Committed scenario',
        value: ({ col }) => (
          <ScenarioOrderTable orderChanges={col.unassignedOrders} />
        ),
      },
    ] as ScenarioComparisonRow[]),
  ];
  return scenarioComparisonRows;
};

export const getScenarioComparisonKPIColumns = (
  comparisonData: ReturnType<UseScenarioComparison>['comparisonData'],
  orderById: Map<number, ScenarioTimeline_OrderFragment>,
  scenarios: readonly ScenarioSchedule[],
  selectedScenarios: ClientId[],
  selectedBaseScenario?: ClientId | undefined,
) => {
  const comparedScenarios = selectedBaseScenario
    ? scenarios
        .filter(
          (scenario) =>
            selectedScenarios.includes(scenario.clientId) ||
            scenario.clientId === selectedBaseScenario,
        )
        .sort((a, b) => {
          if (a.clientId === selectedBaseScenario) return -1;
          if (b.clientId === selectedBaseScenario) return 1;
          return 0;
        })
    : scenarios.filter((scenario) =>
        selectedScenarios.includes(scenario.clientId),
      );

  const baseScenarioData = selectedBaseScenario
    ? comparisonData.get(selectedBaseScenario)
    : null;

  const userScenarios = comparedScenarios.filter((s) => !isMasterScenario(s));

  const commonAssignedOrders =
    userScenarios.length > 1
      ? setIntersection(
          userScenarios
            .map((s) => comparisonData.get(s.clientId)?.assignedOrderIds)
            .filter(isDefined),
        )
      : new Set();

  const calculateDelta = (
    currScenario: ClientId,
    currScenarioValue: number,
    baseScenarioValue: number | undefined,
  ) => {
    const showDelta = baseScenarioData && currScenario !== selectedBaseScenario;
    return showDelta ? currScenarioValue - (baseScenarioValue ?? 0) : null;
  };

  return comparedScenarios.map((scenario) => {
    const data = comparisonData.get(scenario.clientId);

    const assignedOrders: ScenarioOrderChange[] = Array.from(
      data?.assignedOrderIds ?? [],
    )
      .map((oId) => orderById.get(oId))
      .filter(isDefined)
      .map((order) => ({
        order,
        changeType: commonAssignedOrders.has(order.id)
          ? ScenarioOrderChangeType.COMMON_ADDED
          : ScenarioOrderChangeType.ADDED,
      }));

    const unassignedOrders: ScenarioOrderChange[] = Array.from(
      data?.unassignedOrderIds ?? [],
    )
      .map((oId) => orderById.get(oId))
      .filter(isDefined)
      .map((order) => ({
        order,
        changeType: ScenarioOrderChangeType.REMOVED,
      }));

    const totalCargoMass = data?.totalCargoMass ?? 0;
    const totalCargoMassDelta = calculateDelta(
      scenario.clientId,
      totalCargoMass,
      baseScenarioData?.totalCargoMass,
    );

    const totalCargoVolume = data?.totalCargoVolume ?? 0;
    const totalCargoVolumeDelta = calculateDelta(
      scenario.clientId,
      totalCargoVolume,
      baseScenarioData?.totalCargoVolume,
    );

    const averageVesselFillRateMass = data?.averageVesselFillRateMass ?? 0;
    const averageVesselFillRateMassDelta = calculateDelta(
      scenario.clientId,
      averageVesselFillRateMass,
      baseScenarioData?.averageVesselFillRateMass,
    );

    const averageVesselFillRateVolume = data?.averageVesselFillRateVolume ?? 0;
    const averageVesselFillRateVolumeDelta = calculateDelta(
      scenario.clientId,
      averageVesselFillRateVolume,
      baseScenarioData?.averageVesselFillRateVolume,
    );

    const totalTonMiles = data?.totalTonMiles ?? 0;
    const totalTonMilesDelta = calculateDelta(
      scenario.clientId,
      totalTonMiles,
      baseScenarioData?.totalTonMiles,
    );

    const totalBallastDistance = data?.totalBallastDistance ?? 0;
    const totalBallastDistanceDelta = calculateDelta(
      scenario.clientId,
      totalBallastDistance,
      baseScenarioData?.totalBallastDistance,
    );

    const totalLadenDistance = data?.totalLadenDistance ?? 0;
    const totalLadenDistanceDelta = calculateDelta(
      scenario.clientId,
      totalLadenDistance,
      baseScenarioData?.totalLadenDistance,
    );

    const ballastPercentage = data?.ballastPercentage ?? 0;
    const ballastPercentageDelta = calculateDelta(
      scenario.clientId,
      ballastPercentage,
      baseScenarioData?.ballastPercentage,
    );

    const totalDays = msToD(data?.totalDuration ?? 0);
    const totalDaysDelta = calculateDelta(
      scenario.clientId,
      totalDays,
      msToD(baseScenarioData?.totalDuration ?? 0),
    );

    const totalPortDays = msToD(data?.totalPortDuration ?? 0);
    const totalPortDaysDelta = calculateDelta(
      scenario.clientId,
      totalPortDays,
      msToD(baseScenarioData?.totalPortDuration ?? 0),
    );

    const totalSeaDays = msToD(data?.totalSeaDuration ?? 0);
    const totalSeaDaysDelta = calculateDelta(
      scenario.clientId,
      totalSeaDays,
      msToD(baseScenarioData?.totalSeaDuration ?? 0),
    );

    const arrivalHitRate = data?.arrivalHitRate ?? 0;
    const arrivalHitRateDelta = calculateDelta(
      scenario.clientId,
      arrivalHitRate,
      baseScenarioData?.arrivalHitRate,
    );

    const totalChannelCost = data?.totalChannelCost ?? 0;
    const totalChannelCostDelta = calculateDelta(
      scenario.clientId,
      totalChannelCost,
      baseScenarioData?.totalChannelCost,
    );

    const totalTcCost = data?.totalTcCost ?? 0;
    const totalTcCostDelta = calculateDelta(
      scenario.clientId,
      totalTcCost,
      baseScenarioData?.totalTcCost,
    );

    const totalTcCostPerTonMile = data?.totalTcCostPerTonMile ?? 0;
    const totalTcCostPerTonMileDelta = calculateDelta(
      scenario.clientId,
      totalTcCostPerTonMile,
      baseScenarioData?.totalTcCostPerTonMile,
    );

    const effectiveTonMiles = data?.effectiveTonMiles ?? 0;
    const effectiveTonMilesDelta = calculateDelta(
      scenario.clientId,
      effectiveTonMiles,
      baseScenarioData?.effectiveTonMiles,
    );

    const totalBunkerCost = data?.totalBunkerCost ?? 0;
    const totalBunkerCostDelta = calculateDelta(
      scenario.clientId,
      totalBunkerCost,
      baseScenarioData?.totalBunkerCost,
    );

    const totalPortCallCosts = data?.totalPortCallCosts?.totalCosts ?? null;
    const totalPortCallCostsDelta = calculateDelta(
      scenario.clientId,
      totalPortCallCosts ?? 0,
      baseScenarioData?.totalPortCallCosts?.totalCosts,
    );

    const totalReletCost = data?.totalReletCost ?? 0;
    const totalReletCostDelta = calculateDelta(
      scenario.clientId,
      totalReletCost,
      baseScenarioData?.totalReletCost,
    );

    const totalVoyageCost = data?.totalVoyageCost ?? 0;
    const totalVoyageCostDelta = calculateDelta(
      scenario.clientId,
      totalVoyageCost,
      baseScenarioData?.totalVoyageCost,
    );

    const totalCost = data?.totalCost ?? 0;
    const totalCostDelta = calculateDelta(
      scenario.clientId,
      totalCost,
      baseScenarioData?.totalCost,
    );

    const totalCostPerTonMile = data?.totalCostPerTonMile ?? 0;
    const totalCostPerTonMileDelta = calculateDelta(
      scenario.clientId,
      totalCostPerTonMile,
      baseScenarioData?.totalCostPerTonMile,
    );

    const totalIncome = data?.totalIncome ?? 0;
    const totalIncomeDelta = calculateDelta(
      scenario.clientId,
      totalIncome,
      baseScenarioData?.totalIncome,
    );

    const averageTCE = data?.averageTCE ?? 0;
    const averageTCEDelta = calculateDelta(
      scenario.clientId,
      averageTCE,
      baseScenarioData?.averageTCE,
    );

    const totalVoyageProfit =
      (data?.totalIncome ?? 0) - (data?.totalVoyageCost ?? 0);
    const totalVoyageProfitDelta = calculateDelta(
      scenario.clientId,
      totalVoyageProfit,
      (baseScenarioData?.totalIncome ?? 0) -
        (baseScenarioData?.totalVoyageCost ?? 0),
    );

    const totalProfit = (data?.totalIncome ?? 0) - (data?.totalCost ?? 0);
    const totalProfitDelta = calculateDelta(
      scenario.clientId,
      totalProfit,
      (baseScenarioData?.totalIncome ?? 0) - (baseScenarioData?.totalCost ?? 0),
    );

    const totalEeoi = data?.totalEeoi ?? null;
    const totalEeoiDelta =
      totalEeoi !== null && baseScenarioData?.totalEeoi !== null
        ? calculateDelta(
            scenario.clientId,
            totalEeoi,
            baseScenarioData?.totalEeoi,
          )
        : null;

    const totalEts = data?.totalEts ?? null;
    const totalEtsDelta =
      totalEts !== null && baseScenarioData?.totalEts !== null
        ? calculateDelta(
            scenario.clientId,
            totalEts,
            baseScenarioData?.totalEts,
          )
        : null;

    const totalFuelEu = data?.totalFuelEu ?? null;
    const totalFuelEuDelta =
      totalFuelEu !== null && baseScenarioData?.totalFuelEu !== null
        ? calculateDelta(
            scenario.clientId,
            totalFuelEu,
            baseScenarioData?.totalFuelEu,
          )
        : null;

    const fairwayDuesPortCost =
      data?.totalPortCostBreakdown.get('Fairway dues') ?? null;
    const fairwayDuesPortCostDelta =
      fairwayDuesPortCost !== null &&
      baseScenarioData?.totalPortCostBreakdown.get('Fairway dues') !== null
        ? calculateDelta(
            scenario.clientId,
            fairwayDuesPortCost,
            baseScenarioData?.totalPortCostBreakdown.get('Fairway dues'),
          )
        : null;

    const pilotagePortCost =
      data?.totalPortCostBreakdown.get('Pilotage') ?? null;
    const pilotagePortCostDelta =
      pilotagePortCost !== null &&
      baseScenarioData?.totalPortCostBreakdown.get('Pilotage') !== null
        ? calculateDelta(
            scenario.clientId,
            pilotagePortCost,
            baseScenarioData?.totalPortCostBreakdown.get('Pilotage'),
          )
        : null;

    const harborDuesPortCost =
      data?.totalPortCostBreakdown.get('Harbor dues') ?? null;
    const harborDuesPortCostDelta =
      harborDuesPortCost !== null &&
      baseScenarioData?.totalPortCostBreakdown.get('Harbor dues') !== null
        ? calculateDelta(
            scenario.clientId,
            harborDuesPortCost,
            baseScenarioData?.totalPortCostBreakdown.get('Harbor dues'),
          )
        : null;

    const mooringUnmooringPortCost =
      data?.totalPortCostBreakdown.get('Mooring & unmooring') ?? null;
    const mooringUnmooringPortCostDelta =
      mooringUnmooringPortCost !== null &&
      baseScenarioData?.totalPortCostBreakdown.get('Mooring & unmooring') !==
        null
        ? calculateDelta(
            scenario.clientId,
            mooringUnmooringPortCost,
            baseScenarioData?.totalPortCostBreakdown.get('Mooring & unmooring'),
          )
        : null;

    return {
      scenarioId: scenario.clientId,
      scenarioName: scenario.name ?? null,
      arrivalHitRate: {
        value: arrivalHitRate,
        delta: arrivalHitRateDelta,
      },
      assignedOrders,
      averageVesselFillRateMass: {
        value: averageVesselFillRateMass,
        delta: averageVesselFillRateMassDelta,
      },
      averageVesselFillRateVolume: {
        value: averageVesselFillRateVolume,
        delta: averageVesselFillRateVolumeDelta,
      },
      ballastPercentage: {
        value: ballastPercentage,
        delta: ballastPercentageDelta,
      },
      totalBallastDistance: {
        value: totalBallastDistance,
        delta: totalBallastDistanceDelta,
      },
      totalBunkerCost: {
        value: totalBunkerCost,
        delta: totalBunkerCostDelta,
      },
      totalCargoMass: {
        value: totalCargoMass,
        delta: totalCargoMassDelta,
      },
      totalCargoVolume: {
        value: totalCargoVolume,
        delta: totalCargoVolumeDelta,
      },
      totalCost: {
        value: totalCost,
        delta: totalCostDelta,
      },
      totalCostPerTonMile: {
        value: totalCostPerTonMile,
        delta: totalCostPerTonMileDelta,
      },
      totalDays: {
        value: totalDays,
        delta: totalDaysDelta,
      },
      totalEeoi: {
        value: totalEeoi,
        delta: totalEeoiDelta,
      },
      totalEts: {
        value: totalEts,
        delta: totalEtsDelta,
      },
      totalFuelEu: {
        value: totalFuelEu,
        delta: totalFuelEuDelta,
      },
      totalLadenDistance: {
        value: totalLadenDistance,
        delta: totalLadenDistanceDelta,
      },
      fairwayDuesPortCost: {
        value: fairwayDuesPortCost,
        delta: fairwayDuesPortCostDelta,
      },
      pilotagePortCost: {
        value: pilotagePortCost,
        delta: pilotagePortCostDelta,
      },
      harborDuesPortCost: {
        value: harborDuesPortCost,
        delta: harborDuesPortCostDelta,
      },
      mooringUnmooringPortCost: {
        value: mooringUnmooringPortCost,
        delta: mooringUnmooringPortCostDelta,
      },
      totalPortCallCosts: {
        value: totalPortCallCosts,
        delta: totalPortCallCostsDelta,
      },
      totalReletCost: {
        value: totalReletCost,
        delta: totalReletCostDelta,
      },
      totalPortDays: {
        value: totalPortDays,
        delta: totalPortDaysDelta,
      },
      totalProfit: {
        value: totalProfit,
        delta: totalProfitDelta,
      },
      totalSeaDays: {
        value: totalSeaDays,
        delta: totalSeaDaysDelta,
      },
      totalChannelCost: {
        value: totalChannelCost,
        delta: totalChannelCostDelta,
      },
      totalTcCost: {
        value: totalTcCost,
        delta: totalTcCostDelta,
      },
      totalTcCostPerTonMile: {
        value: totalTcCostPerTonMile,
        delta: totalTcCostPerTonMileDelta,
      },
      effectiveTonMiles: {
        value: effectiveTonMiles,
        delta: effectiveTonMilesDelta,
      },
      totalTonMiles: {
        value: totalTonMiles,
        delta: totalTonMilesDelta,
      },
      totalIncome: {
        value: totalIncome,
        delta: totalIncomeDelta,
      },
      totalTCE: data?.totalTCE ?? 0,
      totalVoyageCost: {
        value: totalVoyageCost,
        delta: totalVoyageCostDelta,
      },
      totalVoyageProfit: {
        value: totalVoyageProfit,
        delta: totalVoyageProfitDelta,
      },
      averageTCE: {
        value: averageTCE,
        delta: averageTCEDelta,
      },
      unassignedOrders,
    };
  });
};

type Columns = ReturnType<typeof getScenarioComparisonKPIColumns>;

const isBestValue = <T extends keyof Columns[number]>(
  columns: Columns,
  key: T,
  goodValue: 'high' | 'low',
  idx: number,
) => {
  const obj = columns[idx][key];
  if (
    !obj ||
    typeof obj !== 'object' ||
    !('value' in obj) ||
    obj.value === null
  ) {
    return false;
  }
  const value = obj.value;
  for (const column of columns) {
    const current = column[key];
    if (
      !current ||
      typeof current !== 'object' ||
      !('value' in current) ||
      current.value === null
    ) {
      continue;
    }
    const currentValue = current.value;
    const isBetter =
      goodValue === 'high' ? currentValue > value : currentValue < value;
    if (isBetter) {
      return false;
    }
  }
  return true;
};

export const ScenarioComparisonKPITable = ({
  columns,
  isScenarioLoading,
  selectedBaseScenario,
}: {
  columns: ReturnType<typeof getScenarioComparisonKPIColumns>;
  isScenarioLoading: (id: ClientId) => boolean;
  selectedBaseScenario?: ClientId | undefined;
}): JSX.Element => {
  const { visibleRows } = useScenarioComparisonKPIRows();

  return (
    <Table data-testid="scenario-comparison-table" layout="fixed">
      <thead>
        <TableHeaders isSticky>
          <TableHeader width={225}>&nbsp;</TableHeader>
          {columns.map((col) => (
            <TableHeader
              width={250}
              key={col.scenarioId}
              style={
                selectedBaseScenario === col.scenarioId
                  ? {
                      color: 'black',
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5',
                    }
                  : undefined
              }
            >
              {col.scenarioName}
              {selectedBaseScenario === col.scenarioId && ' (Base)'}
            </TableHeader>
          ))}
        </TableHeaders>
      </thead>
      <tbody>
        {visibleRows.map((row, rowIndex) => {
          const isOrderRow =
            row.key === 'assignedOrders' || row.key === 'unassignedOrders';

          return (
            <TableRow key={row.key} isMuted={rowIndex % 2 !== 0}>
              <TableCell
                data-testid={`${row.header}`}
                scope="row"
                verticalAlign={isOrderRow ? 'top' : 'center'}
                pt={isOrderRow ? '12px' : undefined}
              >
                <Tooltip content={<KpiTooltip {...row} />}>
                  <Text>{row.header}</Text>
                </Tooltip>
              </TableCell>
              {columns.map((col, colIndex) => (
                <TableCell
                  data-testid={`${row.header}-scenario-col:${colIndex}`}
                  key={col.scenarioId}
                  verticalAlign={isOrderRow ? 'top' : 'center'}
                >
                  {row.goodValue &&
                    !isScenarioLoading(col.scenarioId) &&
                    row.key !== 'days' &&
                    isBestValue(columns, row.key, row.goodValue, colIndex) && (
                      <GoodValueDot />
                    )}
                  <Tooltip content={<KpiTooltip {...row} />}>
                    <Text>
                      {row.value({
                        col,
                        loading: isScenarioLoading(col.scenarioId),
                      })}
                    </Text>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export const ComparisonScenariosSelect: React.FC<{
  scenarios: readonly ScenarioSchedule[];
  selectedScenarios: ClientId[];
  selectedBaseScenario?: ClientId | undefined;
  handleScenarioToggleClick: (scenarioId: ClientId) => void;
  handleBaseScenarioToggleClick?: (scenarioId: ClientId) => void;
}> = ({
  scenarios,
  selectedScenarios,
  selectedBaseScenario,
  handleScenarioToggleClick,
  handleBaseScenarioToggleClick,
}) => {
  return (
    <Box>
      <FormField label="Select scenarios">
        <Box display="flex" flexDirection="row">
          {scenarios
            .filter((x) => x.type !== 'backup' && !x.deletedAt)
            .map((scenario) => (
              <Box
                data-testid={`comparison-scenario-button-${scenario.name}`}
                key={scenario.clientId}
                mr={2}
              >
                <Button
                  variant={
                    selectedScenarios.includes(scenario.clientId)
                      ? 'primary'
                      : 'secondary'
                  }
                  onPress={() => handleScenarioToggleClick(scenario.clientId)}
                >
                  {scenario.name}
                </Button>
              </Box>
            ))}
        </Box>
      </FormField>

      {/* Only show base scenario selection when the base scenario
       handler is provided (ScenarioComparison mode) */}
      {handleBaseScenarioToggleClick && (
        <FormField label="Select base scenario">
          <Box display="flex" flexDirection="row">
            {scenarios
              .filter((x) => x.type !== 'backup' && !x.deletedAt)
              .map((scenario) => (
                <Box
                  data-testid={`comparison-base-scenario-button-${scenario.name}`}
                  key={scenario.clientId}
                  mr={2}
                >
                  <Button
                    variant={
                      selectedBaseScenario === scenario.clientId
                        ? 'primary'
                        : 'secondary'
                    }
                    onPress={() =>
                      handleBaseScenarioToggleClick(scenario.clientId)
                    }
                  >
                    {scenario.name}
                  </Button>
                </Box>
              ))}
          </Box>
        </FormField>
      )}
    </Box>
  );
};

export const ComparisonModeAndKPIsSelect: React.FC<{
  view: string;
  setView: React.Dispatch<React.SetStateAction<'timeline' | 'kpi'>>;
}> = ({ view, setView }) => {
  const { allRows, hiddenRows, onRowToggleChange } =
    useScenarioComparisonKPIRows();

  return (
    <>
      <FormField label="View" width="auto">
        <MenuToggle
          type="radio"
          label="KPIs"
          checked={view === 'kpi'}
          onChange={() => setView('kpi')}
        />
        <MenuToggle
          type="radio"
          label="Timeline"
          checked={view === 'timeline'}
          onChange={() => setView('timeline')}
        />
      </FormField>
      <FormField label="KPIs" width="auto">
        <DropdownButton
          label="Select KPIs"
          variant="secondary"
          closeOnContentClick={false}
          width="100%"
        >
          <Menu hasShadow>
            <Box display="flex" flexDirection="column" width={240}>
              {allRows.map((row) => (
                <Box
                  as="label"
                  key={row.key}
                  display="flex"
                  alignItems="center"
                  mb={1}
                >
                  <input
                    type="checkbox"
                    checked={!hiddenRows.includes(row.key)}
                    onChange={(e) =>
                      onRowToggleChange(row.key, !e.target.checked)
                    }
                  />
                  <Text ml={2} isUnselectable>
                    {row.header}
                  </Text>
                </Box>
              ))}
            </Box>
          </Menu>
        </DropdownButton>
      </FormField>
    </>
  );
};

type ScheduleMap = Map<
  string,
  | {
      type: 'spot' | 'tc';
      title: string;
    }
  | {
      type: 'coa';
      title: string;
      id: number;
    }
>;

const scheduleMapAtom = atom<ScheduleMap>(new Map());
const selectedSchedulesAtom = atom({
  tc: true,
  coas: new Set<number>(),
  spots: new Set<string>(),
});

function ScheduleSelect() {
  const currentUser = useCurrentUser();
  const isShipOwner = currentUser?.organization?.shipOwner;

  const schedules = useAtomValue(scheduleMapAtom);
  const [selectedSchedules, setSelectedSchedules] = useAtom(
    selectedSchedulesAtom,
  );

  const setInitialValues = useCallback(() => {
    setSelectedSchedules((prev) => {
      const newCoas = new Set(prev.coas);
      const newSpots = new Set(prev.spots);
      schedules.forEach((value, key) => {
        if (value.type === 'coa') {
          newCoas.add(value.id);
        } else if (value.type === 'spot') {
          newSpots.add(key);
        }
      });
      return { coas: newCoas, spots: newSpots, tc: true };
    });
  }, [schedules, setSelectedSchedules]);

  useOnce(() => {
    setInitialValues();
  }, schedules.size > 0);

  const { coaContracts, spotContracts } = useMemo(() => {
    const coaIds = new Set<number>();
    const spotTitles = new Set<string>();
    const coaResult: { id: number; title: string }[] = [];
    const spotResult: { id: string; title: string }[] = [];

    [...schedules.entries()].forEach(([key, value]) => {
      if (value.type === 'coa' && value.id !== null && !coaIds.has(value.id)) {
        coaIds.add(value.id);
        coaResult.push({ id: value.id, title: value.title });
      } else if (value.type === 'spot' && !spotTitles.has(value.title)) {
        spotTitles.add(value.title);
        spotResult.push({ id: key, title: value.title });
      }
    });

    return { coaContracts: coaResult, spotContracts: spotResult };
  }, [schedules]);

  return (
    <>
      <FormField label="Schedules" width="auto">
        <DropdownButton
          label="Select Schedule"
          variant="secondary"
          closeOnContentClick={false}
          width="100%"
        >
          <Menu hasShadow maxHeight="70dvh" overflowY="auto">
            <Box p={2} mb={1}>
              <Text
                fontSize={1}
                fontWeight="bold"
                color="neutral.gray_340"
                isUnselectable
              >
                Filter by Fleet
              </Text>
            </Box>
            <Box as="label" display="flex" alignItems="center" mt={1}>
              <input
                type="checkbox"
                checked={selectedSchedules.tc}
                onChange={() => {
                  setSelectedSchedules((prev) => ({
                    ...prev,
                    tc: !prev.tc,
                  }));
                }}
              />
              <Text ml={2} isUnselectable>
                All Fleet
              </Text>
            </Box>
            {coaContracts.length > 0 ? (
              <>
                <MenuSeparator />
                <Box p={2} mb={1}>
                  <Text
                    fontSize={1}
                    fontWeight="bold"
                    color="neutral.gray_340"
                    isUnselectable
                  >
                    Filter by COAs
                  </Text>
                </Box>
                <Box as="label" display="flex" alignItems="center" mb={1}>
                  <input
                    type="checkbox"
                    checked={
                      selectedSchedules.coas.size === coaContracts.length
                    }
                    onChange={() => {
                      setSelectedSchedules((prev) => ({
                        ...prev,
                        coas: new Set(
                          prev.coas.size === coaContracts.length
                            ? []
                            : coaContracts.map(({ id }) => id),
                        ),
                      }));
                    }}
                  />
                  <Text ml={2} isUnselectable>
                    Toggle all COAs
                  </Text>
                </Box>
                <Box display="flex" flexDirection="column" width="max-content">
                  {coaContracts.map(({ id, title }) => (
                    <Box
                      key={id}
                      as="label"
                      display="flex"
                      alignItems="center"
                      mb={1}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSchedules.coas.has(id)}
                        onChange={() => {
                          setSelectedSchedules((prev) => {
                            const newCoas = new Set(prev.coas);
                            if (newCoas.has(id)) {
                              newCoas.delete(id);
                            } else {
                              newCoas.add(id);
                            }
                            return { ...prev, coas: newCoas };
                          });
                        }}
                      />
                      <Text ml={2} isUnselectable>
                        {title}
                      </Text>
                    </Box>
                  ))}
                </Box>
                <MenuSeparator />
              </>
            ) : null}
            {spotContracts.length > 0 ? (
              <>
                <Box p={2} mb={1}>
                  <Text
                    fontSize={1}
                    fontWeight="bold"
                    color="neutral.gray_340"
                    isUnselectable
                  >
                    Filter by {isShipOwner ? 'RELETs' : 'SPOTs'}
                  </Text>
                </Box>
                <Box as="label" display="flex" alignItems="center" mb={1}>
                  <input
                    type="checkbox"
                    checked={
                      selectedSchedules.spots.size === spotContracts.length
                    }
                    onChange={() => {
                      setSelectedSchedules((prev) => ({
                        ...prev,
                        spots: new Set(
                          prev.spots.size === spotContracts.length
                            ? []
                            : spotContracts.map(({ id }) => id),
                        ),
                      }));
                    }}
                  />
                  <Text ml={2} isUnselectable>
                    Toggle all {isShipOwner ? 'RELETs' : 'SPOTs'}
                  </Text>
                </Box>
                <Box display="flex" flexDirection="column" width="max-content">
                  {spotContracts.map(({ id, title }) => (
                    <Box
                      key={id}
                      as="label"
                      display="flex"
                      alignItems="center"
                      mb={1}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSchedules.spots.has(id)}
                        onChange={() => {
                          setSelectedSchedules((prev) => {
                            const newSpots = new Set(prev.spots);
                            if (newSpots.has(id)) {
                              newSpots.delete(id);
                            } else {
                              newSpots.add(id);
                            }
                            return { ...prev, spots: newSpots };
                          });
                        }}
                      />
                      <Text ml={2} isUnselectable>
                        {title}
                      </Text>
                    </Box>
                  ))}
                </Box>
              </>
            ) : null}
            <Box p={2} mt={3}>
              <Button
                variant="secondary"
                onPress={() => {
                  setInitialValues();
                }}
              >
                Reset
              </Button>
            </Box>
          </Menu>
        </DropdownButton>
      </FormField>
    </>
  );
}

const ScenarioComparison = ({
  isOpen,
  onClose,
  scenarios,
  vessels: allVessels,
  orderById,
  calculationConfig,
  currentUserOrgId,
  organizationsById,
  selectedScenarioId,
  masterData,
  selectedScenarioData,
  committedData,
  getScenarioData,
  onResetAssignmentsToMaster,
  onConfirmEventOverwrite,
  sharedOrderIds,
  scheduleContext,
  pnlData,
}: ScenarioComparisonProps): JSX.Element => {
  const vessels = useMemo(
    () => allVessels.filter((v) => !v.externalFleet),
    [allVessels],
  );

  const [selectedBaseScenario, setSelectedBaseScenario] = useState<
    ClientId | undefined
  >(undefined);

  const handleBaseScenarioToggleClick = useEvent((scenarioId: ClientId) => {
    if (selectedBaseScenario === scenarioId) {
      setSelectedBaseScenario(undefined);
    } else {
      setSelectedBaseScenario(scenarioId);
    }
  });

  const {
    addScenarioToComparison,
    comparisonData,
    dateRange,
    loadingScenarios,
    removeScenarioFromComparison,
    selectedScenarios,
    setDateRange,
    useVesselFiltersResult,
    incomeByScenario,
    reletCostsByScenario,
    isAutomaticFilteringEnabled,
    setIsAutomaticFilteringEnabled,
    subTimelinesByScenarioCId,
    calculateSubTimeline,
  } = useScenarioComparison(
    scenarios,
    vessels,
    getScenarioData,
    calculationConfig,
    pnlData,
    selectedScenarioId,
    selectedBaseScenario,
  );

  const columns = useMemo<ScenarioComparisonColumn[]>(
    () =>
      getScenarioComparisonKPIColumns(
        comparisonData,
        orderById,
        scenarios,
        selectedScenarios,
        selectedBaseScenario,
      ),
    [
      comparisonData,
      orderById,
      scenarios,
      selectedBaseScenario,
      selectedScenarios,
    ],
  );

  const handleScenarioToggleClick = useEvent((scenarioId: ClientId) => {
    return selectedScenarios.includes(scenarioId)
      ? removeScenarioFromComparison(scenarioId)
      : addScenarioToComparison(scenarioId);
  });

  const onDateFilterChange = useEvent((val: [Date, Date] | null) => {
    if (val !== null) {
      const [startDate, endDate] = val;
      setDateRange({ start: startDate.valueOf(), end: endDate.valueOf() });
    }
  });

  const onAutomaticFilteringToggle = useEvent((newValue: boolean) => {
    if (newValue && selectedScenarios.length === 0) {
      addScenarioToComparison(MASTER_SCENARIO_CLIENTID);
      addScenarioToComparison(selectedScenarioId);
    }

    setIsAutomaticFilteringEnabled(newValue);
  });

  const hasOwnVessels = vessels.some(
    (vessel) => vessel.organizationId === currentUserOrgId,
  );

  const [view, setView] = useState<'kpi' | 'timeline'>('kpi');

  const isScenarioLoading = useCallback(
    (scenarioId: ClientId) =>
      loadingScenarios.has(scenarioId) ||
      !incomeByScenario.has(scenarioId) ||
      !reletCostsByScenario.has(scenarioId),
    [incomeByScenario, loadingScenarios, reletCostsByScenario],
  );

  return (
    <Modal width="100%" height="100%" isOpen={isOpen} onClose={onClose}>
      <ModalBody p={0} overflow="hidden" width="100%">
        <Box bg="neutral.white_100" p={4} width="100%">
          <FormRow>
            <ComparisonScenariosSelect
              scenarios={scenarios}
              selectedBaseScenario={selectedBaseScenario}
              selectedScenarios={selectedScenarios}
              handleScenarioToggleClick={handleScenarioToggleClick}
              handleBaseScenarioToggleClick={handleBaseScenarioToggleClick}
            />
          </FormRow>
          <FormRow>
            <ComparisonModeAndKPIsSelect view={view} setView={setView} />
            <ScheduleSelect />
            {hasOwnVessels ? (
              <FormField
                data-testid="Filter-vessels-drop"
                label="Vessels"
                width="auto"
              >
                <FilterVessels
                  vessels={vessels}
                  useVesselFiltersResult={useVesselFiltersResult}
                  onChange={() => setIsAutomaticFilteringEnabled(false)}
                />
              </FormField>
            ) : null}
            <FormField label="Date range" width="auto">
              <Box
                borderColor="neutral.gray_225"
                borderWidth={1}
                borderStyle="solid"
              >
                <DateRangePicker
                  timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                  start={dateRange.start}
                  end={dateRange.end}
                  onChange={onDateFilterChange}
                  placement="bottom-end"
                  isControlled
                />
              </Box>
            </FormField>
            <FormField label="Options" width="auto">
              <Box as="label" display="flex" alignItems="center">
                <input
                  data-testid="automatic-filtering"
                  type="checkbox"
                  checked={isAutomaticFilteringEnabled}
                  onChange={(e) =>
                    onAutomaticFilteringToggle(e.currentTarget.checked)
                  }
                />
                <Text ml={2} isUnselectable>
                  Use automatic filtering
                </Text>
              </Box>
            </FormField>
          </FormRow>
        </Box>
        <Box width="100%" height="100%" overflow="auto">
          {view === 'kpi' ? (
            <ScenarioComparisonKPITable
              columns={columns}
              isScenarioLoading={isScenarioLoading}
              selectedBaseScenario={selectedBaseScenario}
            />
          ) : (
            <CompareScenarioTimelines
              scheduleContext={scheduleContext}
              organizations={organizationsById}
              masterData={masterData}
              selectedScenarioData={selectedScenarioData}
              selectedScenarios={selectedScenarios}
              timelinesByScenario={subTimelinesByScenarioCId}
              calculateSubTimeline={calculateSubTimeline}
              scenarios={scenarios}
              committedData={committedData}
              onResetAssignmentsToMaster={onResetAssignmentsToMaster}
              onConfirmEventOverwrite={onConfirmEventOverwrite}
              sharedOrderIds={sharedOrderIds}
              getScenarioData={getScenarioData}
            />
          )}
        </Box>
      </ModalBody>
    </Modal>
  );
};

export default ScenarioComparison;
